/**
 * Humo Chat Widget
 *
 * Embeddable chat widget for customer websites.
 * Similar to Crisp/Chatwoot - lightweight, customizable, easy to integrate.
 */

import './styles.css';
import { currentPage, linkifyEscaped, renderListingCards, type ListingCard, type ListingMore } from './listingCards';
import { io, Socket } from 'socket.io-client';
import { poweredByUrl } from './poweredBy';
import { browserInfo, cleanContext, rememberVisit, type SiteContext } from './visitorInfo';

export interface WidgetConfig {
  websiteId: string;
  apiUrl?: string;
  position?: 'bottom-right' | 'bottom-left';
  primaryColor?: string;
  secondaryColor?: string;
  fontFamily?: string;
  fontSize?: 'small' | 'medium' | 'large';
  greeting?: string;
  multilingualGreeting?: boolean; // Enable/disable multilingual greeting
  greetingLanguages?: string[]; // Custom language list (optional)
  placeholder?: string;
  launcherIcon?: 'chat' | 'message' | 'help';
  autoOpen?: boolean;
  visitorName?: string;
  visitorEmail?: string;
  /** Who is chatting, as the site knows it (member, chosen place); later changes via setContext. */
  context?: SiteContext | null;
  locale?: 'en' | 'tr';
  enableFileUpload?: boolean;
  enableEmojis?: boolean;
  enableVoiceRecording?: boolean;
  enableVoiceCall?: boolean;   // Phase 4: live voice call with AI (distinct from voice-note)
  maxFileSize?: number; // in bytes, default 5MB
  allowedFileTypes?: string[]; // e.g. ['image/*', 'application/pdf']
  maxRecordingDuration?: number; // in seconds, default 120s (2 minutes)
}

interface Attachment {
  id: string;
  name: string;
  type: string;
  size: number;
  url?: string;
  previewUrl?: string;
  duration?: number; // for audio attachments
  blob?: Blob; // for pending uploads
}

interface SocketMessage {
  type: 'message' | 'typing' | 'status';
  id?: string;
  text?: string;
  sender?: 'user' | 'agent' | 'bot';
  timestamp?: string | number;
  isTyping?: boolean;
  messageId?: string;
  status?: Message['status'];
  attachments?: any[];
}

interface HistoryResponse {
  messages?: Array<{
    id: string;
    raw_text?: string;
    text?: string;
    direction?: 'INBOUND' | 'OUTBOUND';
    sender?: 'customer' | 'agent' | 'bot';
    created_at?: string;
    timestamp?: string;
    attachments?: any[];
    listingCards?: ListingCard[];
    listingMore?: ListingMore;
  }>;
}

interface UploadResponse {
  id: string;
  url: string;
  name: string;
  type: string;
  size: number;
}

interface SendMessageResponse {
  success: boolean;
  conversationId?: string;
  contactRequest?: ContactRequestPayload;
  autoReply?: {
    messageId?: string;
    text?: string;
    timestamp?: string;
    actionButtons?: ActionButton[];
  };
}

interface ContactRequestPayload {
  message?: string;
  fields: {
    name: boolean;
    email: boolean;
  };
  optional: boolean;
  language?: string;
}

interface ContactSubmitResponse {
  success: boolean;
  message?: string;
}

interface ActionButton {
  label: string;
  action: 'send_message' | 'handoff' | 'open_url';
  payload?: string;
}


interface Message {
  id: string;
  text: string;
  sender: 'user' | 'agent' | 'bot';
  timestamp: Date;
  status?: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
  attachments?: Attachment[];
  actionButtons?: ActionButton[];
  integrationCard?: {
    id?: string;
    status?: string;
    trackingNumber?: string;
    estimatedDelivery?: string;
    items?: string;
    rawSummary?: string;
  };
  listingCards?: ListingCard[];
  listingMore?: ListingMore;
}

interface VoiceTranscriptEntry {
  segmentId: string;
  text: string;
  speakerRole: 'customer' | 'agent' | 'ai';
  isFinal: boolean;
}

interface WidgetState {
  isOpen: boolean;
  isMinimized: boolean;
  messages: Message[];
  unreadCount: number;
  isTyping: boolean;
  connectionStatus: 'connecting' | 'connected' | 'disconnected';
  agentOnline: boolean;
  showEmojiPicker: boolean;
  pendingAttachments: Attachment[];
  draftText: string;
  isRecording: boolean;
  recordingDuration: number;
  audioVisualizerData: number[];
  showContactForm: boolean;
  contactFormMessage: string;
  contactFormFields: { name: boolean; email: boolean };
  showCsatForm: boolean;
  csatRating: number;
  csatComment: string;
  csatSubmitted: boolean;
  csatConversationId: string;
  handoffActive: boolean;
  handoffConversationId: string;
  helpfulPromptMessageId: string | null;
  // Phase 4: Live voice call
  voiceCallActive: boolean;
  voiceCallStatus: 'idle' | 'connecting' | 'active' | 'ended';
  voiceCallSessionId: string | null;
  voiceCallParticipantId: string | null;
  voiceCallMuted: boolean;
  voiceCallTranscript: VoiceTranscriptEntry[];
  voiceCallAiText: string | null;
  voiceCallConsentGiven: boolean;
  showVoiceConsentDialog: boolean;
  /** Agent-initiated call ringing — set when chat socket receives
   *  voice:incoming_call. Cleared on accept (call starts) or decline. */
  incomingAgentCall: {
    voiceSessionId: string;
    agentName: string;
    orgName?: string;
  } | null;
  /**
   * Fine-grained turn state for the UI. Listening = mic open, waiting for
   * speech. Thinking = customer finished, server is generating a reply
   * (intentionally distinct from "speaking" because there's typically a
   * 1-3s gap and dead air feels broken). Speaking = AI audio is playing.
   * Error = something went wrong (timeout, server error) — see voiceTurnError
   * for the human-readable cause.
   */
  voiceTurnPhase: 'listening' | 'thinking' | 'speaking' | 'error';
  voiceTurnError: string | null;
}

const DEFAULT_CONFIG: Required<WidgetConfig> = {
  websiteId: '',
  apiUrl: '',
  position: 'bottom-right',
  primaryColor: '#6366f1',
  secondaryColor: '#f3f4f6',
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  fontSize: 'medium',
  greeting: 'Hi there! How can we help you today?',
  multilingualGreeting: true,
  greetingLanguages: [],
  placeholder: 'Type a message...',
  launcherIcon: 'chat',
  autoOpen: false,
  visitorName: '',
  visitorEmail: '',
  // Null: the site gave none, so none is sent and Hümo keeps what it had.
  context: null,
  locale: 'en',
  enableFileUpload: true,
  enableEmojis: true,
  enableVoiceRecording: true,
  enableVoiceCall: true,   // Phase 4: live voice call enabled by default
  maxFileSize: 5 * 1024 * 1024,
  allowedFileTypes: ['image/*', 'application/pdf', '.doc', '.docx', '.txt'],
  maxRecordingDuration: 120
};

// Common emojis for quick picker
const EMOJI_LIST = [
  '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂',
  '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩',
  '😘', '😗', '😚', '😙', '🥲', '😋', '😛', '😜',
  '🤪', '😝', '🤑', '🤗', '🤭', '🤫', '🤔', '🤐',
  '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙',
  '👋', '🤚', '🖐️', '✋', '👏', '🙌', '👐', '🤝',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍',
  '💯', '💥', '💫', '⭐', '🌟', '✨', '💪', '🎉'
];

// Multilingual greeting options
const MULTILINGUAL_GREETINGS = ['Merhaba', 'Hello', 'Привет', '你好', 'Hola', 'مرحبا'];

// Shared by both render paths (full rebuild and the messages-only fast path),
// so a single message's markup is identical no matter how it was produced.
const SVG_SPARKLE = `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L13.8 9.2L21 12L13.8 14.8L12 22L10.2 14.8L3 12L10.2 9.2L12 2Z"/><path d="M20 4L20.6 6.4L23 7L20.6 7.6L20 10L19.4 7.6L17 7L19.4 6.4L20 4Z" opacity="0.55"/><path d="M4 16L4.5 17.5L6 18L4.5 18.5L4 20L3.5 18.5L2 18L3.5 17.5L4 16Z" opacity="0.4"/></svg>`;
const SVG_AGENT_PERSON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`;

const TRANSLATIONS = {
  en: {
    greeting: 'Hi there! How can we help you today?',
    multilingualGreeting: MULTILINGUAL_GREETINGS.join(' · '),
    placeholder: 'Type a message...',
    sendButton: 'Send',
    connecting: 'Connecting...',
    connected: 'Connected',
    disconnected: 'Disconnected',
    typingIndicator: 'Agent is typing...',
    poweredBy: 'Powered by Hümo',
    agentOnline: 'Agent Online',
    aiAssistant: 'AI Assistant',
    contactNamePlaceholder: 'Your name',
    contactEmailPlaceholder: 'Your email address',
    contactSubmit: 'Send',
    contactSkip: 'Not now',
    csatTitle: 'How was your experience?',
    csatSubmit: 'Submit',
    csatSkip: 'Skip',
    csatThanks: 'Thank you for your feedback!',
    csatCommentPlaceholder: 'Any additional comments? (optional)',
    voiceConsentTitle: 'Start a voice call?',
    voiceConsentBody: 'Your microphone will be used so Hümo can hear and respond in real time.',
    voiceConsentPrivacy: 'Calls may be processed and stored according to this business\'s voice settings.',
    voiceConsentConfirm: 'Start call',
    voiceConsentCancel: 'Not now',
    voiceCallLabel: 'Voice Call',
    voiceCallConnecting: 'Connecting voice call…',
    voiceCallConnectingHint: 'Joining the voice room and checking microphone access.',
    voiceCallMuted: 'Muted',
    voiceCallMicOn: 'Mic On',
    voiceCallLive: 'Live',
    voiceCallTranscript: 'Live transcript',
    voiceCallTranscriptHint: 'Updates as the conversation happens.',
    voiceCallAiSpeaking: 'AI speaking…',
    voiceCallListening: 'Listening…',
    voiceCallThinking: 'Preparing response…',
    voiceCallTimeout: 'No response in time. Tap to retry.',
    voiceCallError: 'Something went wrong. Tap to retry.',
    voiceCallRetry: 'Retry',
    voiceCallTrigger: 'Call'
  },
  tr: {
    greeting: 'Merhaba! Size nasıl yardımcı olabiliriz?',
    multilingualGreeting: MULTILINGUAL_GREETINGS.join(' · '),
    placeholder: 'Mesajınızı yazın...',
    sendButton: 'Gönder',
    connecting: 'Bağlanıyor...',
    connected: 'Bağlandı',
    disconnected: 'Bağlantı kesildi',
    typingIndicator: 'Temsilci yazıyor...',
    poweredBy: 'Hümo ile çalışır',
    agentOnline: 'Temsilci Çevrimiçi',
    aiAssistant: 'AI Asistan',
    contactNamePlaceholder: 'İsminiz',
    contactEmailPlaceholder: 'E-posta adresiniz',
    contactSubmit: 'Gönder',
    contactSkip: 'Şimdi değil',
    csatTitle: 'Deneyiminizi nasıl değerlendirirsiniz?',
    csatSubmit: 'Gönder',
    csatSkip: 'Geç',
    csatThanks: 'Geri bildiriminiz için teşekkürler!',
    csatCommentPlaceholder: 'Eklemek istediğiniz bir şey var mı? (isteğe bağlı)',
    voiceConsentTitle: 'Sesli arama başlatılsın mı?',
    voiceConsentBody: 'Hümo sizi gerçek zamanlı duymak ve yanıtlamak için mikrofonunuza erişecek.',
    voiceConsentPrivacy: 'Aramalar, işletmenin ses ayarlarına göre işlenebilir ve saklanabilir.',
    voiceConsentConfirm: 'Aramayı başlat',
    voiceConsentCancel: 'Şimdi değil',
    voiceCallLabel: 'Sesli Arama',
    voiceCallConnecting: 'Sesli arama bağlanıyor…',
    voiceCallConnectingHint: 'Ses odasına katılıyor ve mikrofon izni kontrol ediliyor.',
    voiceCallMuted: 'Sessizde',
    voiceCallMicOn: 'Mikrofon Açık',
    voiceCallLive: 'Canlı',
    voiceCallTranscript: 'Canlı döküm',
    voiceCallTranscriptHint: 'Konuşma sırasında anlık güncellenir.',
    voiceCallAiSpeaking: 'AI konuşuyor…',
    voiceCallListening: 'Dinleniyor…',
    voiceCallThinking: 'Yanıt hazırlanıyor…',
    voiceCallTimeout: 'Yanıt gelmedi. Tekrar deneyin.',
    voiceCallError: 'Bir sorun oluştu. Tekrar deneyin.',
    voiceCallRetry: 'Tekrar dene',
    voiceCallTrigger: 'Ara'
  }
};

class HumoWidget {
  private config!: Required<WidgetConfig>;
  private state!: WidgetState;
  private container: HTMLDivElement | null = null;
  private sessionId!: string;
  private storageAvailable = false;
  private memoryStorage: Record<string, string> = {};
  private socket: Socket | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private audioChunks: Blob[] = [];
  private recordingTimer: number | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private visualizerAnimation: number | null = null;
  private typingTimeout: number | null = null;
  private typingThrottleTimer: number | null = null;
  private isTypingSent = false;
  private objectUrls: Set<string> = new Set();
  private abortController: AbortController | null = null;
  private renderThrottleTimer: number | null = null;
  private pendingRender = false;
  private viewportListenersBound = false;
  // Signature of all "shell" state (everything outside the messages list).
  // When this is unchanged between renders we take the fast path and update
  // ONLY the messages list — leaving the input/header DOM (and its focus,
  // caret, scroll) untouched. This is what kills the flicker + focus loss.
  private lastShellSignature: string | null = null;
  // ISO timestamp of the most recent message we've shown. Used on socket
  // reconnect to pull anything we missed via GET /widget/conversations/:s/?since=.
  private lastSeenMessageAt: string | null = null;
  // Runtime config fetched from GET /widget/config: org-configured greeting +
  // business-persona starter questions generated from the knowledge base.
  private remoteGreeting: string | null = null;
  private starterQuestions: string[] = [];
  private remoteCompanyName: string | null = null;
  // Phase 4: Voice call
  private voiceSocket: Socket | null = null;
  private voiceMediaRecorder: MediaRecorder | null = null;
  private voiceMediaStream: MediaStream | null = null;
  /**
   * Watchdog timer that fires if AI never responds after the customer
   * finishes talking. Set when we receive voice:transcript:final for the
   * customer; cleared when voice:ai:response or voice:ai:audio_chunk arrives.
   * Fallback to error state so the UI doesn't pretend something is happening.
   */
  private voiceTurnTimeout: number | null = null;
  private readonly VOICE_TURN_TIMEOUT_MS = 15_000;
  private voiceChunkIndex = 0;
  private voiceAudioQueue: string[] = [];
  /** Currently-playing AI voice clip; non-null while AI is speaking. Used by
   *  barge-in to stop playback when the user starts a new turn. */
  private currentVoiceAudio: HTMLAudioElement | null = null;
  /**
   * Persistent <audio> element primed during the call-start user gesture.
   *
   * Mobile browsers (iOS Safari, Android Chrome) refuse to play() audio
   * outside a user-gesture context. We create + play a silent buffer on this
   * element when the user taps "Start call", which "unlocks" the element so
   * subsequent .src changes can play AI responses without rejection. Without
   * this, mobile users see the widget transcribing their speech but never
   * hear the AI's reply — backend logs show a successful audio chunk emit
   * but the play() promise is silently rejected on the client.
   */
  private voicePlaybackAudio: HTMLAudioElement | null = null;
  private voicePlaybackUnlocked = false;
  /** AudioContext + analyser for mic VAD (barge-in). Created on first call. */
  private vadContext: AudioContext | null = null;
  private vadAnalyser: AnalyserNode | null = null;
  private vadRaf: number | null = null;
  private voiceAudioPlaying = false;
  private readonly handleViewportChange = () => {
    this.updateViewportMetrics();
  };
  private readonly handleFocusIn = (event: FocusEvent) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !this.isTrackedInteractiveElement(target)) {
      return;
    }

    window.setTimeout(() => {
      this.updateViewportMetrics();
      this.ensureFocusedFieldVisible(target);
    }, 60);
    window.setTimeout(() => {
      this.updateViewportMetrics();
      this.ensureFocusedFieldVisible(target);
    }, 280);
  };
  private readonly handleFocusOut = () => {
    window.setTimeout(() => {
      this.updateViewportMetrics();
    }, 120);
  };

  constructor(config: WidgetConfig) {
    if (!config.websiteId) {
      throw new Error('[HUMO Widget] websiteId is required');
    }

    this.config = { ...DEFAULT_CONFIG, ...config };
    if (this.config.context) this.config.context = cleanContext(this.config.context);
    rememberVisit();
    this.state = {
      isOpen: this.config.autoOpen,
      isMinimized: false,
      messages: [],
      unreadCount: 0,
      isTyping: false,
      connectionStatus: 'connecting',
      agentOnline: false,
      showEmojiPicker: false,
      pendingAttachments: [],
      draftText: '',
      isRecording: false,
      recordingDuration: 0,
      audioVisualizerData: [],
      showContactForm: false,
      contactFormMessage: '',
      contactFormFields: { name: false, email: false },
      showCsatForm: false,
      csatRating: 0,
      csatComment: '',
      csatSubmitted: false,
      csatConversationId: '',
      handoffActive: false,
      handoffConversationId: '',
      helpfulPromptMessageId: null,
      voiceCallActive: false,
      voiceCallStatus: 'idle',
      voiceCallSessionId: null,
      voiceCallParticipantId: null,
      voiceCallMuted: false,
      voiceCallTranscript: [],
      voiceCallAiText: null,
      voiceCallConsentGiven: false,
      showVoiceConsentDialog: false,
      incomingAgentCall: null,
      voiceTurnPhase: 'listening',
      voiceTurnError: null
    };

    this.storageAvailable = this.canUseLocalStorage();

    // Get or create session ID
    this.sessionId = this.getOrCreateSessionId();

    // Initialize widget
    this.init();
  }

  private canUseLocalStorage(): boolean {
    if (typeof window === 'undefined' || !window.localStorage) {
      return false;
    }

    try {
      const probeKey = '__humo_storage_probe__';
      window.localStorage.setItem(probeKey, '1');
      window.localStorage.removeItem(probeKey);
      return true;
    } catch (error) {
      console.warn('[HUMO Widget] localStorage unavailable, falling back to in-memory session.', error);
      return false;
    }
  }

  private readFromStorage(key: string): string | null {
    if (!this.storageAvailable) {
      return this.memoryStorage[key] || null;
    }

    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      this.storageAvailable = false;
      console.warn('[HUMO Widget] localStorage read failed, using in-memory session.', error);
      return this.memoryStorage[key] || null;
    }
  }

  private writeToStorage(key: string, value: string): void {
    if (!this.storageAvailable) {
      this.memoryStorage[key] = value;
      return;
    }

    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      this.storageAvailable = false;
      this.memoryStorage[key] = value;
      console.warn('[HUMO Widget] localStorage write failed, using in-memory session.', error);
    }
  }

  private getOrCreateSessionId(): string {
    const storageKey = `humo_session_${this.config.websiteId}`;
    let sessionId = this.readFromStorage(storageKey);

    if (!sessionId) {
      sessionId = `visitor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      this.writeToStorage(storageKey, sessionId);
    }

    return sessionId;
  }

  // ── Voice session resume (route-change / refresh tolerance) ───────────────
  //
  // The backend keeps a voice session alive for a short grace window after a
  // customer socket drops (see VOICE_DISCONNECT_GRACE_MS in
  // voiceSocketService). When the widget script re-runs (SPA route change,
  // refresh, etc.) we re-attach to that pending session instead of POSTing
  // a brand-new /voice/start and losing transcript / orchestrator memory.
  private readonly VOICE_RESUME_TTL_MS = 25_000; // intentionally < backend grace (30s)

  private getVoiceResumeStorageKey(): string {
    return `humo_voice_session_${this.config.websiteId}`;
  }

  private storeVoiceSession(voiceSessionId: string): void {
    try {
      this.writeToStorage(
        this.getVoiceResumeStorageKey(),
        JSON.stringify({ sessionId: voiceSessionId, ts: Date.now() })
      );
    } catch { /* ignore */ }
  }

  private clearStoredVoiceSession(): void {
    try {
      if (this.storageAvailable) {
        window.localStorage.removeItem(this.getVoiceResumeStorageKey());
      } else {
        delete this.memoryStorage[this.getVoiceResumeStorageKey()];
      }
    } catch { /* ignore */ }
  }

  private readStoredVoiceSession(): string | null {
    const raw = this.readFromStorage(this.getVoiceResumeStorageKey());
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as { sessionId?: string; ts?: number };
      if (!parsed.sessionId || !parsed.ts) return null;
      if (Date.now() - parsed.ts > this.VOICE_RESUME_TTL_MS) {
        this.clearStoredVoiceSession();
        return null;
      }
      return parsed.sessionId;
    } catch {
      this.clearStoredVoiceSession();
      return null;
    }
  }

  private t(key: keyof typeof TRANSLATIONS.en): string {
    return TRANSLATIONS[this.config.locale][key] || TRANSLATIONS.en[key];
  }

  private init(): void {
    // Create container
    this.container = document.createElement('div');
    this.container.id = 'humo-widget-container';
    this.container.className = `humo-widget humo-${this.config.position}`;
    document.body.appendChild(this.container);

    // Set CSS custom properties
    this.container.style.setProperty('--humo-primary', this.config.primaryColor);
    this.container.style.setProperty('--humo-bg-secondary', this.config.secondaryColor);
    this.container.style.setProperty('--humo-font', this.config.fontFamily);

    // Set font size based on config
    const fontSizeMap = {
      small: '14px',
      medium: '15px',
      large: '16px'
    };
    this.container.style.setProperty('--humo-font-size', fontSizeMap[this.config.fontSize]);
    this.bindViewportEvents();
    this.updateViewportMetrics();

    // Render widget
    this.render();

    // Fetch runtime config (greeting + persona starter questions) — async,
    // re-renders the welcome block when it lands.
    this.loadRemoteConfig();

    // Connect WebSocket
    this.connectWebSocket();

    // Load message history
    this.loadHistory();

    // Attempt to resume an in-flight voice call from a previous widget
    // instance (route change, refresh) — skip if call already active.
    const resumeId = this.readStoredVoiceSession();
    if (resumeId && this.state.voiceCallStatus === 'idle') {
      // startVoiceCall handles consent guarding; for a resume we know the
      // user already consented in this site, so call it with the stored id.
      this.state.voiceCallConsentGiven = true;
      this.startVoiceCall({ resumeSessionId: resumeId }).catch((err) => {
        console.warn('[HUMO Voice] Resume failed, clearing stored session', err);
        this.clearStoredVoiceSession();
      });
    }
  }

  private connectWebSocket(): void {
    if (!this.config.apiUrl) {
      // Demo mode - no Socket.IO
      this.state.connectionStatus = 'connected';
      this.render();
      return;
    }

    // Don't attempt to connect if offline
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this.state.connectionStatus = 'disconnected';
      this.render();
      this.setupOnlineListener();
      return;
    }

    try {
      // Connect to Socket.IO /widget namespace
      this.socket = io(`${this.config.apiUrl}/widget`, {
        query: {
          socketContext: 'widget',
          websiteId: this.config.websiteId
        },
        transports: ['polling', 'websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 10000
      });

      this.socket.on('connect', () => {
        console.log('[HUMO Widget] Connected to Socket.IO');
        this.state.connectionStatus = 'connected';

        // Send join event with session info
        this.socket?.emit('join', {
          sessionId: this.sessionId,
          websiteId: this.config.websiteId,
          visitorName: this.config.visitorName,
          visitorEmail: this.config.visitorEmail
        });

        this.render();

        // Catchup-pull anything we missed during the disconnect window.
        // Socket.IO doesn't replay missed events after reconnect, so on a
        // mobile suspend / WiFi drop / route change the customer would
        // otherwise lose any agent replies sent in that gap.
        void this.catchupMissedMessages();
      });

      this.socket.on('joined', (data: { sessionId: string; timestamp: Date }) => {
        console.log('[HUMO Widget] Session joined:', data);
      });

      this.socket.on('message', (data: SocketMessage & { integrationCard?: Message['integrationCard']; actionButtons?: ActionButton[]; listingCards?: ListingCard[]; listingMore?: ListingMore }) => {
        this.handleSocketMessage({ ...data, type: 'message' });
      });

      this.socket.on('typing', (data: { isTyping: boolean; agentName?: string }) => {
        this.handleSocketMessage({ type: 'typing', isTyping: data.isTyping });
      });

      this.socket.on('status', (data: { messageId: string; status: Message['status'] }) => {
        this.handleSocketMessage({ type: 'status', messageId: data.messageId, status: data.status });
      });

      this.socket.on('message_read', (data: { messageId: string; readAt: string }) => {
        this.handleSocketMessage({ type: 'status', messageId: data.messageId, status: 'read' });
      });

      this.socket.on('presence', (data: { agentOnline: boolean }) => {
        this.state.agentOnline = data.agentOnline;
        this.render();
        console.log('[HUMO Widget] Agent presence:', data.agentOnline ? 'online' : 'offline');
      });

      this.socket.on('request_contact', (data: { message?: string; fields: { name: boolean; email: boolean }; optional: boolean; language?: string }) => {
        this.showContactRequest(data);
      });

      this.socket.on('contact_received', (data: { success: boolean; message: string }) => {
        this.handleContactConfirmation(data);
      });

      this.socket.on('request_csat', (data: { conversationId: string; language?: string }) => {
        if (data.language && (data.language === 'tr' || data.language === 'en')) {
          this.config.locale = data.language;
        }
        this.state.showCsatForm = true;
        this.state.csatConversationId = data.conversationId;
        this.state.csatRating = 0;
        this.state.csatComment = '';
        this.state.csatSubmitted = false;
        this.render();
        this.scrollToBottom();
      });

      // Agent-initiated voice call: dashboard called us. The chat socket
      // is always-on so this works even when the voice namespace isn't
      // connected yet. Show an accept prompt; on accept, start the same
      // voice call flow as customer-initiated but with resumeSessionId so
      // we don't double-create the voice session row.
      this.socket.on(
        'voice:incoming_call',
        (data: { voiceSessionId: string; agentName?: string; orgName?: string }) => {
          if (!data?.voiceSessionId) return;
          console.log('[HUMO Voice] incoming agent call', data);
          this.state.incomingAgentCall = {
            voiceSessionId: data.voiceSessionId,
            agentName: data.agentName || 'Agent',
            orgName: data.orgName
          };
          // Pop the widget open so the customer can see the ring.
          this.state.isOpen = true;
          this.state.isMinimized = false;
          this.playIncomingRingtone();
          this.render();
        }
      );

      this.socket.on('csat_received', () => {
        this.state.csatSubmitted = true;
        this.render();
        setTimeout(() => {
          this.state.showCsatForm = false;
          this.render();
        }, 3000);
      });

      // Human handoff: agent is taking over the conversation
      this.socket.on('handoff', (data: { conversationId: string; message?: string; language?: string }) => {
        if (data.language && (data.language === 'tr' || data.language === 'en')) {
          this.config.locale = data.language;
        }
        this.state.handoffActive = true;
        this.state.handoffConversationId = data.conversationId || '';
        this.state.agentOnline = true;
        // Optionally add a system message
        if (data.message) {
          this.addMessage({
            id: `handoff_${Date.now()}`,
            text: data.message,
            sender: 'bot',
            timestamp: new Date()
          });
        }
        this.render();
        this.scrollToBottom();
      });

      this.socket.on('disconnect', (reason: string) => {
        console.log('[HUMO Widget] Disconnected:', reason);
        this.state.connectionStatus = 'disconnected';
        this.render();
      });

      this.socket.on('connect_error', (error: Error) => {
        console.error('[HUMO Widget] Connection error:', error);
        this.state.connectionStatus = 'disconnected';
        this.render();
      });

      this.socket.on('error', (error: any) => {
        console.error('[HUMO Widget] Socket error:', error);
      });

    } catch (e) {
      console.error('[HUMO Widget] Socket.IO connection failed', e);
      this.state.connectionStatus = 'disconnected';
      this.render();
    }
  }

  private setupOnlineListener(): void {
    const handleOnline = () => {
      if (this.state.connectionStatus === 'disconnected') {
        this.connectWebSocket();
      }
    };
    window.addEventListener('online', handleOnline, { once: true });
  }

  private buildWidgetApiUrl(path: string, params: Record<string, string | undefined> = {}): string {
    const base = this.config.apiUrl.endsWith('/') ? this.config.apiUrl.slice(0, -1) : this.config.apiUrl;
    const url = new URL(`${base}${path}`);
    url.searchParams.set('websiteId', this.config.websiteId);

    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        url.searchParams.set(key, value);
      }
    });

    return url.toString();
  }

  private isMobileViewport(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 480px)').matches;
  }

  private isTrackedInteractiveElement(target: HTMLElement): boolean {
    return Boolean(target.closest('.humo-input, .humo-contact-input, .humo-csat-comment'));
  }

  private bindViewportEvents(): void {
    if (!this.container || this.viewportListenersBound || typeof window === 'undefined') {
      return;
    }

    this.viewportListenersBound = true;
    window.addEventListener('resize', this.handleViewportChange);
    this.container.addEventListener('focusin', this.handleFocusIn);
    this.container.addEventListener('focusout', this.handleFocusOut);
    window.visualViewport?.addEventListener('resize', this.handleViewportChange);
    window.visualViewport?.addEventListener('scroll', this.handleViewportChange);
  }

  private unbindViewportEvents(): void {
    if (!this.container || !this.viewportListenersBound || typeof window === 'undefined') {
      return;
    }

    this.viewportListenersBound = false;
    window.removeEventListener('resize', this.handleViewportChange);
    this.container.removeEventListener('focusin', this.handleFocusIn);
    this.container.removeEventListener('focusout', this.handleFocusOut);
    window.visualViewport?.removeEventListener('resize', this.handleViewportChange);
    window.visualViewport?.removeEventListener('scroll', this.handleViewportChange);
  }

  private updateViewportMetrics(): void {
    if (!this.container || !this.isMobileViewport()) {
      this.container?.classList.remove('humo-keyboard-open');
      this.container?.style.removeProperty('--humo-mobile-viewport-height');
      this.container?.style.removeProperty('--humo-viewport-offset-top');
      return;
    }

    const visualViewport = window.visualViewport;
    const viewportHeight = Math.round(visualViewport?.height || window.innerHeight);
    const offsetTop = Math.max(0, Math.round(visualViewport?.offsetTop || 0));
    const keyboardOpen = Boolean(
      visualViewport &&
      window.innerHeight - visualViewport.height > 120
    );

    this.container.style.setProperty('--humo-mobile-viewport-height', `${viewportHeight}px`);
    this.container.style.setProperty('--humo-viewport-offset-top', `${offsetTop}px`);
    this.container.classList.toggle('humo-keyboard-open', keyboardOpen);
  }

  private ensureFocusedFieldVisible(field?: HTMLElement | null): void {
    if (!this.container || !this.state.isOpen || !this.isMobileViewport()) {
      return;
    }

    const target = field || (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    if (!target || !this.container.contains(target)) {
      return;
    }

    requestAnimationFrame(() => {
      this.scrollToBottom();
      target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  }

  private showContactRequest(request?: ContactRequestPayload | null): void {
    if (!request?.message) {
      return;
    }

    if (request.language && (request.language === 'tr' || request.language === 'en')) {
      this.config.locale = request.language;
    }

    this.state.showContactForm = true;
    this.state.contactFormMessage = request.message;
    this.state.contactFormFields = request.fields;
    this.render();
    this.scrollToBottom();

    window.setTimeout(() => {
      const firstField = this.container?.querySelector('.humo-contact-input') as HTMLInputElement | null;
      firstField?.focus();
    }, 80);
  }

  private handleContactConfirmation(response?: ContactSubmitResponse | null): void {
    if (!response?.success) {
      return;
    }

    this.state.showContactForm = false;
    if (response.message) {
      this.addMessage({
        id: `system_${Date.now()}`,
        text: response.message,
        sender: 'bot',
        timestamp: new Date()
      });
    } else {
      this.render();
    }
  }

  private async submitContact(payload: { name?: string; email?: string }): Promise<void> {
    if (payload.name) this.config.visitorName = payload.name;
    if (payload.email) this.config.visitorEmail = payload.email;

    if (this.socket && this.socket.connected) {
      this.socket.emit('submit_contact', {
        sessionId: this.sessionId,
        name: payload.name,
        email: payload.email
      });
      return;
    }

    if (!this.config.apiUrl) {
      this.showError('Unable to save contact details right now.');
      return;
    }

    try {
      const response = await fetch(this.buildWidgetApiUrl('/widget/contact'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.config.websiteId
        },
        body: JSON.stringify({
          sessionId: this.sessionId,
          name: payload.name,
          email: payload.email
        })
      });

      if (!response.ok) {
        throw new Error(`Contact submission failed with status ${response.status}`);
      }

      const data = await response.json() as ContactSubmitResponse;
      this.handleContactConfirmation(data);
    } catch (error) {
      console.error('[HUMO Widget] Contact submission failed', error);
      this.showError('Failed to save your contact details. Please try again.');
    }
  }

  // Note: attemptReconnect removed - Socket.IO handles reconnection automatically

  private handleSocketMessage(data: SocketMessage & { integrationCard?: Message['integrationCard']; actionButtons?: ActionButton[]; listingCards?: ListingCard[]; listingMore?: ListingMore }): void {
    switch (data.type) {
      case 'message':
        const socketAttachments = this.normalizeAttachments(data.attachments);
        const newMsg: Message = {
          id: data.id || `msg_${Date.now()}`,
          text: data.text || '',
          sender: data.sender || 'agent',
          timestamp: new Date(data.timestamp || Date.now()),
          attachments: socketAttachments.length > 0 ? socketAttachments : undefined,
          actionButtons: data.actionButtons,
          integrationCard: data.integrationCard,
          listingCards: Array.isArray(data.listingCards) ? data.listingCards : undefined,
          listingMore: data.listingMore
        };
        this.addMessage(newMsg);
        // Track last bot message for "Was this helpful?" prompt
        if (newMsg.sender === 'bot') {
          this.state.helpfulPromptMessageId = newMsg.id;
        }
        if (!this.state.isOpen) {
          this.state.unreadCount++;
        }
        break;

      case 'typing':
        this.state.isTyping = data.isTyping || false;
        this.render();
        break;

      case 'status':
        // Update message status efficiently
        const msg = this.state.messages.find(m => m.id === data.messageId);
        if (msg && data.status) {
          msg.status = data.status;
          this.updateMessageStatus(data.messageId, data.status);
        }
        break;
    }
  }

  private updateMessageStatus(messageId: string | undefined, status: Message['status']): void {
    if (!this.container || !messageId) return;
    const messageEl = this.container.querySelector(`[data-message-id="${messageId}"]`);
    if (messageEl) {
      const statusEl = messageEl.querySelector('.humo-status');
      if (statusEl) {
        statusEl.innerHTML = this.getStatusIcon(status);
      }
    }
  }

  private async loadRemoteConfig(): Promise<void> {
    if (!this.config.apiUrl) return;

    try {
      const response = await fetch(this.buildWidgetApiUrl('/widget/config'), {
        headers: {
          'X-API-Key': this.config.websiteId
        }
      });
      if (!response.ok) return;

      const data = await response.json() as {
        company_name?: string;
        greeting?: { tr?: string | null; en?: string | null };
        starter_questions?: { tr?: string[]; en?: string[] };
      };

      const locale = this.config.locale === 'tr' ? 'tr' : 'en';
      this.remoteCompanyName = data.company_name || null;
      this.remoteGreeting = this.config.greeting !== DEFAULT_CONFIG.greeting
        ? this.config.greeting // explicit embed-config greeting wins
        : (data.greeting?.[locale] || null);
      const questions = data.starter_questions?.[locale];
      this.starterQuestions = Array.isArray(questions) ? questions.slice(0, 6) : [];

      // Refresh the welcome block if it is on screen. Force the full render
      // path — the fast path never replaces an already-mounted welcome block,
      // and the starter chips need their click bindings.
      if (this.state.messages.length === 0) {
        this.lastShellSignature = null;
        this.render();
      }
    } catch {
      // Non-fatal: widget works with built-in defaults when config fetch fails
    }
  }

  private async loadHistory(): Promise<void> {
    if (!this.config.apiUrl) return;

    try {
      const response = await fetch(
        this.buildWidgetApiUrl(`/widget/conversations/${this.sessionId}`),
        {
          headers: {
            'X-API-Key': this.config.websiteId
          }
        }
      );

      if (response.ok) {
        const data = await response.json() as HistoryResponse;
        if (data.messages && Array.isArray(data.messages)) {
          this.state.messages = data.messages.map((m) => {
            let sender: 'user' | 'agent' | 'bot';
            if (m.direction === 'INBOUND') {
              sender = 'user';
            } else if (m.sender === 'bot') {
              sender = 'bot';
            } else {
              sender = 'agent';
            }
            return {
              id: m.id,
              text: m.raw_text || m.text || '',
              sender,
              timestamp: new Date(m.created_at || m.timestamp || Date.now()),
              status: 'read' as const,
              attachments: this.normalizeAttachments(m.attachments),
              listingCards: Array.isArray(m.listingCards) ? m.listingCards : undefined,
              listingMore: m.listingMore
            };
          });
          this.render();
        }
      }
    } catch (e) {
      console.error('[HUMO Widget] Failed to load history', e);
    }
  }

  /**
   * Pull messages we missed during a socket disconnect.
   *
   * Triggered from the socket `connect` event. Calls the widget conversation
   * REST endpoint with `?since=<lastSeenMessageAt>`; backend returns only
   * messages newer than that timestamp. Adds them via addMessage which
   * dedupes by id, so this is safe even if a later socket replay overlaps.
   *
   * Silently no-ops on the very first connect (no lastSeenMessageAt yet)
   * and on network failure — the REST endpoint is non-critical.
   */
  private async catchupMissedMessages(): Promise<void> {
    if (!this.lastSeenMessageAt) return;
    if (!this.config.apiUrl) return;

    try {
      const url = this.buildWidgetApiUrl(
        `/widget/conversations/${this.sessionId}`,
        { since: this.lastSeenMessageAt }
      );
      const response = await fetch(url, {
        headers: { 'X-API-Key': this.config.websiteId }
      });
      if (!response.ok) return;
      const data = await response.json() as {
        messages?: Array<{
          id: string;
          text?: string;
          raw_text?: string;
          sender?: 'customer' | 'agent' | 'bot';
          timestamp?: string;
          created_at?: string;
          status?: Message['status'];
          attachments?: Attachment[];
          listingCards?: ListingCard[];
          listingMore?: ListingMore;
        }>;
      };

      const incoming = Array.isArray(data?.messages) ? data.messages : [];
      let appended = 0;
      for (const m of incoming) {
        const tsStr = m.timestamp || m.created_at;
        if (!tsStr) continue;
        const senderType: Message['sender'] = m.sender === 'customer'
          ? 'user'
          : (m.sender === 'agent' ? 'agent' : 'bot');
        this.addMessage({
          id: m.id,
          text: m.text || m.raw_text || '',
          sender: senderType,
          timestamp: new Date(tsStr),
          status: m.status || 'delivered',
          attachments: m.attachments,
          listingCards: Array.isArray(m.listingCards) ? m.listingCards : undefined,
          listingMore: m.listingMore
        });
        appended += 1;
      }
      if (appended > 0) {
        console.log(`[HUMO Widget] Catchup replayed ${appended} message(s) since ${this.lastSeenMessageAt}`);
      }
    } catch (err) {
      console.warn('[HUMO Widget] Catchup failed:', err);
    }
  }

  private addMessage(message: Message): void {
    // Dedupe by id — catchup-pull on reconnect may overlap with what the
    // socket later replays, and we don't want duplicate bubbles.
    if (message.id && this.state.messages.some(m => m.id === message.id)) {
      return;
    }
    this.state.messages.push(message);

    // Track newest timestamp so reconnect catchup knows where to resume.
    const ts = message.timestamp instanceof Date
      ? message.timestamp.toISOString()
      : (typeof message.timestamp === "string" ? message.timestamp : null);
    if (ts && (!this.lastSeenMessageAt || ts > this.lastSeenMessageAt)) {
      this.lastSeenMessageAt = ts;
    }

    this.render();
    this.scrollToBottom();
  }

  private async sendMessage(text: string): Promise<void> {
    if (!text.trim() && this.state.pendingAttachments.length === 0) return;

    const message: Message = {
      id: `msg_${Date.now()}`,
      text: text.trim(),
      sender: 'user',
      timestamp: new Date(),
      status: 'sending',
      attachments: this.state.pendingAttachments.length > 0 ? [...this.state.pendingAttachments] : undefined
    };

    const attachmentsToSend = [...this.state.pendingAttachments];
    this.state.pendingAttachments = [];
    this.addMessage(message);

    // Prepare FormData if there are attachments
    const hasAttachments = attachmentsToSend.length > 0;
    let uploadedAttachments: UploadResponse[] = [];

    if (hasAttachments && this.config.apiUrl) {
      // Upload attachments first
      try {
        for (const att of attachmentsToSend) {
          if (att.blob) {
            const formData = new FormData();
            formData.append('file', att.blob, att.name);
            formData.append('sessionId', this.sessionId);
            formData.append('websiteId', this.config.websiteId);

            const uploadResponse = await fetch(this.buildWidgetApiUrl('/widget/upload'), {
              method: 'POST',
              headers: {
                'X-API-Key': this.config.websiteId
              },
              body: formData
            });

            if (uploadResponse.ok) {
              const uploadData = await uploadResponse.json();
              const normalized = this.normalizeUploadResponse(uploadData);
              if (normalized) {
                uploadedAttachments.push(normalized);
              }
            } else {
              throw new Error(`Upload failed for ${att.name}`);
            }
          }
        }
      } catch (e) {
        console.error('[HUMO Widget] File upload failed', e);
        message.status = 'failed';
        this.render();
        this.showError('Failed to upload attachments. Please try again.');
        return;
      }
    }

    if (uploadedAttachments.length > 0 && message.attachments) {
      message.attachments = message.attachments.map(att => {
        const match = uploadedAttachments.find(uploaded => uploaded.name === att.name && uploaded.size === att.size);
        if (!match) return att;
        return {
          ...att,
          id: match.id || att.id,
          url: match.url || att.url
        };
      });
      this.render();
    }

    // Send message via Socket.IO if connected
    if (this.socket && this.socket.connected) {
      this.socket.emit('message', {
        sessionId: this.sessionId,
        websiteId: this.config.websiteId,
        text: text.trim(),
        visitorName: this.config.visitorName,
        visitorEmail: this.config.visitorEmail,
        attachments: uploadedAttachments,
        page: currentPage(),
        visitor: browserInfo(),
        ...(this.config.context ? { context: this.config.context } : {})
      });
      message.status = 'sent';
      this.render();
    } else if (this.config.apiUrl) {
      // Fallback to HTTP
      try {
        this.state.isTyping = true;
        this.render();

        const response = await fetch(this.buildWidgetApiUrl('/widget/messages'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': this.config.websiteId
          },
          body: JSON.stringify({
            sessionId: this.sessionId,
            text: text.trim(),
            customerName: this.config.visitorName,
            visitorName: this.config.visitorName,
            visitorEmail: this.config.visitorEmail,
            attachments: uploadedAttachments,
            metadata: {
              source: 'chat_widget_http'
            }
          })
        });

        if (response.ok) {
          const data = await response.json() as SendMessageResponse;
          message.status = 'sent';
          if (data.autoReply?.text) {
            this.addMessage({
              id: data.autoReply.messageId || `bot_${Date.now()}`,
              text: data.autoReply.text,
              sender: 'bot',
              timestamp: new Date(data.autoReply.timestamp || Date.now()),
              actionButtons: data.autoReply.actionButtons
            });
          }
          if (data.contactRequest) {
            this.showContactRequest(data.contactRequest);
          }
        } else {
          message.status = 'failed';
        }
        this.state.isTyping = false;
        this.render();
      } catch (e) {
        message.status = 'failed';
        this.state.isTyping = false;
        this.render();
      }
    } else {
      // Demo mode - simulate response
      message.status = 'sent';
      this.render();

      setTimeout(() => {
        this.addMessage({
          id: `bot_${Date.now()}`,
          text: 'Thanks for your message! This is a demo response. Connect to your HUMO backend to enable real conversations.',
          sender: 'bot',
          timestamp: new Date()
        });
      }, 1000);
    }
  }

  private scrollToBottom(): void {
    // Use double RAF to ensure DOM is painted
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const messagesContainer = this.container?.querySelector('.humo-messages');
        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      });
    });
  }

  private formatTime(date: Date): string {
    return date.toLocaleTimeString(this.config.locale === 'tr' ? 'tr-TR' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private formatRelativeTime(date: Date): string {
    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return this.config.locale === 'tr' ? 'az önce' : 'Just now';
    if (diffMin < 60) return this.config.locale === 'tr' ? `${diffMin}d önce` : `${diffMin}m ago`;
    return this.formatTime(date);
  }

  private formatDuration(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  private resolveAttachmentUrl(url?: string): string | undefined {
    if (!url) return undefined;

    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('blob:')) {
      return url;
    }

    if (!this.config.apiUrl) {
      return url;
    }

    const base = this.config.apiUrl.endsWith('/') ? this.config.apiUrl.slice(0, -1) : this.config.apiUrl;
    const path = url.startsWith('/') ? url : `/${url}`;
    return `${base}${path}`;
  }

  private normalizeUploadResponse(uploadData: any): UploadResponse | null {
    if (!uploadData) return null;
    const file = uploadData.file || uploadData;
    if (!file || !file.url || !file.name) return null;

    return {
      id: file.id || file.fileId || file.filename || file.name,
      url: file.url,
      name: file.name || file.originalName || file.filename,
      type: file.type || file.mimeType || file.contentType || '',
      size: typeof file.size === 'number' ? file.size : Number(file.size || 0)
    };
  }

  private normalizeAttachments(rawAttachments?: any[] | null): Attachment[] {
    if (!rawAttachments || !Array.isArray(rawAttachments)) return [];

    return rawAttachments.map((raw, index) => {
      const file = raw?.file || raw || {};
      const type = file.type || file.mimeType || file.contentType || '';
      const name = file.name || file.filename || file.originalName || 'Attachment';
      const size = typeof file.size === 'number' ? file.size : Number(file.size || 0);
      const url = this.resolveAttachmentUrl(file.url || file.previewUrl || file.fileUrl);
      const duration = typeof file.duration === 'number' ? file.duration : undefined;

      const attachment: Attachment = {
        id: file.id || `att_${Date.now()}_${index}`,
        name,
        type,
        size,
        url,
        previewUrl: url && (type.startsWith('image/') || type.startsWith('audio/')) ? url : undefined,
        duration
      };

      return attachment;
    });
  }

  private renderMessageAttachments(attachments: Attachment[]): string {
    if (!attachments.length) return '';

    return `
      <div class="humo-message-attachments">
        ${attachments.map(att => {
          const type = att.type || '';
          const url = this.resolveAttachmentUrl(att.url || att.previewUrl);
          const safeName = this.escapeHtml(att.name || 'Attachment');
          const safeUrl = url ? this.escapeAttribute(url) : '';

          if (type.startsWith('image/') && url) {
            return `
              <div class="humo-message-attachment humo-message-attachment-image">
                <img src="${safeUrl}" alt="${safeName}" />
              </div>
            `;
          }

          if (type.startsWith('audio/') && url) {
            return `
              <div class="humo-message-attachment humo-message-attachment-audio">
                <audio controls src="${safeUrl}"></audio>
                ${att.duration ? `<span class="humo-audio-duration">${this.formatDuration(att.duration)}</span>` : ''}
              </div>
            `;
          }

          if (url) {
            return `
              <div class="humo-message-attachment humo-message-attachment-file">
                <a href="${safeUrl}" target="_blank" rel="noopener">📎 ${safeName}</a>
              </div>
            `;
          }

          return `
            <div class="humo-message-attachment humo-message-attachment-file">
              <span>📎 ${safeName}</span>
            </div>
          `;
        }).join('')}
      </div>
    `;
  }

  private renderIntegrationCard(card: NonNullable<Message['integrationCard']>): string {
    const isTr = this.config.locale === 'tr';
    const rows: string[] = [];

    if (card.id) {
      rows.push(`<div class="humo-query-card-row"><span class="humo-query-card-label">${isTr ? 'ID:' : 'ID:'}</span><span class="humo-query-card-value">${this.escapeHtml(String(card.id))}</span></div>`);
    }
    if (card.status) {
      rows.push(`<div class="humo-query-card-row"><span class="humo-query-card-label">${isTr ? 'Durum:' : 'Status:'}</span><span class="humo-query-card-value humo-query-card-status">${this.escapeHtml(String(card.status))}</span></div>`);
    }
    if (card.trackingNumber) {
      rows.push(`<div class="humo-query-card-row"><span class="humo-query-card-label">${isTr ? 'Takip No:' : 'Tracking:'}</span><span class="humo-query-card-value">${this.escapeHtml(String(card.trackingNumber))}</span></div>`);
    }
    if (card.estimatedDelivery) {
      rows.push(`<div class="humo-query-card-row"><span class="humo-query-card-label">${isTr ? 'Tahmini:' : 'Est. delivery:'}</span><span class="humo-query-card-value">${this.escapeHtml(String(card.estimatedDelivery))}</span></div>`);
    }
    if (card.items) {
      rows.push(`<div class="humo-query-card-row"><span class="humo-query-card-label">${isTr ? 'Ürünler:' : 'Items:'}</span><span class="humo-query-card-value">${this.escapeHtml(String(card.items)).substring(0, 120)}</span></div>`);
    }
    if (card.rawSummary && rows.length === 0) {
      rows.push(`<div class="humo-query-card-row"><span class="humo-query-card-value" style="white-space:pre-wrap;font-size:11px">${this.escapeHtml(card.rawSummary)}</span></div>`);
    }

    if (rows.length === 0) return '';

    return `
      <div class="humo-query-card">
        <div class="humo-query-card-header">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
          ${isTr ? 'Sorgu Sonucu' : 'Lookup Result'}
        </div>
        ${rows.join('')}
      </div>
    `;
  }

  private async startRecording(): Promise<void> {
    // Check browser support
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.showError('Voice recording is not supported in your browser.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.mediaStream = stream;

      // Create audio context for visualization
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      this.analyser = this.audioContext.createAnalyser();
      const source = this.audioContext.createMediaStreamSource(stream);
      source.connect(this.analyser);
      this.analyser.fftSize = 256;

      // Start media recorder
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      this.mediaRecorder = new MediaRecorder(stream, { mimeType });
      this.audioChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = () => {
        const audioBlob = new Blob(this.audioChunks, { type: mimeType });
        this.handleAudioRecorded(audioBlob);
        this.cleanupRecording();
      };

      this.mediaRecorder.start();
      this.state.isRecording = true;
      this.state.recordingDuration = 0;
      this.state.isOpen = true;
      this.state.isMinimized = false;

      // Start recording timer — update timer text directly, no full re-render
      this.recordingTimer = window.setInterval(() => {
        this.state.recordingDuration++;
        if (this.state.recordingDuration >= this.config.maxRecordingDuration) {
          this.stopRecording();
          return;
        }
        const timerEl = this.container?.querySelector<HTMLElement>('.humo-recording-time');
        if (timerEl) {
          timerEl.textContent = this.formatDuration(this.state.recordingDuration);
        }
      }, 1000);

      // Start visualizer
      this.startVisualizer();
      this.render();
    } catch (error) {
      console.error('[HUMO Widget] Failed to start recording:', error);
      const err = error as Error;
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        this.showError('Microphone permission denied. Please allow access to record voice messages.');
      } else {
        this.showError('Could not access microphone. Please check your device settings.');
      }
    }
  }

  private cleanupRecording(): void {
    // Stop all tracks
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }

    // Close audio context
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
      this.audioContext = null;
    }

    this.analyser = null;
  }

  private stopRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
    if (this.visualizerAnimation) {
      cancelAnimationFrame(this.visualizerAnimation);
      this.visualizerAnimation = null;
    }
    this.state.isRecording = false;
    this.state.audioVisualizerData = [];
    this.render();
  }

  private startVisualizer(): void {
    if (!this.analyser) return;

    const bufferLength = this.analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const updateVisualizer = () => {
      if (!this.analyser || !this.state.isRecording) return;

      this.visualizerAnimation = requestAnimationFrame(updateVisualizer);
      this.analyser.getByteFrequencyData(dataArray);

      // Sample 20 bars from frequency data
      const barCount = 20;
      const step = Math.floor(bufferLength / barCount);
      const visualData: number[] = [];

      for (let i = 0; i < barCount; i++) {
        const index = i * step;
        visualData.push(dataArray[index] / 255); // Normalize to 0-1
      }

      this.state.audioVisualizerData = visualData;

      // Update bars directly in DOM — avoid full re-render at 60fps
      // which would cause the entire widget to flash/flicker.
      if (this.container) {
        const bars = this.container.querySelectorAll<HTMLElement>('.humo-visualizer-bar');
        bars.forEach((bar, i) => {
          bar.style.height = `${Math.max(4, (visualData[i] ?? 0) * 100)}%`;
        });
      }
    };

    updateVisualizer();
  }

  private handleAudioRecorded(audioBlob: Blob): void {
    const objectUrl = URL.createObjectURL(audioBlob);
    this.objectUrls.add(objectUrl);

    const attachment: Attachment = {
      id: `audio_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: `voice-message-${Date.now()}.${audioBlob.type.split('/')[1]}`,
      type: audioBlob.type,
      size: audioBlob.size,
      duration: this.state.recordingDuration,
      blob: audioBlob,
      previewUrl: objectUrl
    };

    this.state.pendingAttachments.push(attachment);
    this.render();
  }

  private cancelRecording(): void {
    // Stop recording without saving
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      // Remove onstop handler to prevent saving
      this.mediaRecorder.onstop = null;
      this.mediaRecorder.stop();
    }
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
    if (this.visualizerAnimation) {
      cancelAnimationFrame(this.visualizerAnimation);
      this.visualizerAnimation = null;
    }

    // Clean up resources
    this.cleanupRecording();

    // Clear audio chunks without saving
    this.audioChunks = [];
    this.state.isRecording = false;
    this.state.recordingDuration = 0;
    this.state.audioVisualizerData = [];
    this.render();
  }

  // ── Phase 4: Live Voice Call ─────────────────────────────────────────────

  private handleVoiceCallTrigger(): void {
    if (this.state.voiceCallStatus !== 'idle') return;

    // Audio unlock MUST happen synchronously in the user-gesture context.
    // This sets up + primes a reusable <audio> element so the AI's reply
    // can play later when no gesture is active. See voicePlaybackAudio doc.
    this.primeVoicePlaybackAudio();

    if (this.state.voiceCallConsentGiven) {
      this.startVoiceCall();
      return;
    }

    this.state.showVoiceConsentDialog = true;
    this.render();
  }

  private confirmVoiceConsent(): void {
    // Consent confirmation is also a user gesture — re-prime in case the
    // initial trigger was from a different code path or browser cleared
    // the unlocked state while the dialog was open.
    this.primeVoicePlaybackAudio();
    this.state.voiceCallConsentGiven = true;
    this.state.showVoiceConsentDialog = false;
    this.startVoiceCall();
  }

  /**
   * Loop a soft ring tone while an agent-initiated call is pending.
   * Cleared by acceptIncomingCall / declineIncomingCall / endVoiceCall.
   * Built from a base64 silent-then-beep MP3 so we don't need a separate
   * asset request that could be blocked by autoplay or CSP.
   */
  private incomingRingAudio: HTMLAudioElement | null = null;
  private playIncomingRingtone(): void {
    try {
      if (this.incomingRingAudio) return;
      // Simple double-beep encoded inline; loops while answering.
      const beep =
        'data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4LjI5LjEwMAAAAAAAAAAAAAAA//tQwAAAAAAA';
      const a = new Audio(beep);
      a.loop = true;
      a.volume = 0.5;
      a.play().catch(() => {
        /* autoplay blocked is fine — the visual ring still shows */
      });
      this.incomingRingAudio = a;
    } catch {
      /* non-fatal */
    }
  }
  private stopIncomingRingtone(): void {
    if (this.incomingRingAudio) {
      try {
        this.incomingRingAudio.pause();
      } catch {
        /* ignore */
      }
      this.incomingRingAudio = null;
    }
  }

  /**
   * Customer clicked "Accept" on an incoming agent-initiated call.
   * Re-uses the existing startVoiceCall path with a resume hint so the
   * backend joins the same voiceSession the agent already created.
   */
  private acceptIncomingCall(): void {
    const incoming = this.state.incomingAgentCall;
    if (!incoming) return;
    this.stopIncomingRingtone();
    this.primeVoicePlaybackAudio();
    // Mark consent as given for this session — accepting the call IS the
    // consent gesture, so the modal doesn't pop on top of the live call.
    this.state.voiceCallConsentGiven = true;
    this.state.incomingAgentCall = null;
    this.render();
    this.startVoiceCall({ resumeSessionId: incoming.voiceSessionId });
  }

  private declineIncomingCall(): void {
    this.stopIncomingRingtone();
    this.state.incomingAgentCall = null;
    this.render();
    // No backend "decline" wire yet — the agent will see the call as
    // RINGING then time out on their side. Adding an explicit decline
    // ack is a small follow-up.
  }

  /**
   * Create (if missing) and play a silent buffer on the persistent voice
   * playback Audio element. Must run inside a user-gesture stack frame —
   * iOS Safari blocks .play() otherwise. After one successful play during
   * a gesture, the element stays unlocked for the rest of the page lifetime
   * and subsequent .src reassignments will play without rejection.
   *
   * A 0.05s silent MP3 (data URI, ~250 bytes base64) is enough to satisfy
   * the unlock requirement without producing audible output.
   */
  private primeVoicePlaybackAudio(): void {
    try {
      if (!this.voicePlaybackAudio) {
        this.voicePlaybackAudio = new Audio();
        this.voicePlaybackAudio.preload = 'auto';
        this.voicePlaybackAudio.setAttribute('playsinline', 'true');
      }
      if (this.voicePlaybackUnlocked) return;
      // 50ms of silence, MP3-encoded. Smallest portable MP3 frame.
      const SILENT_MP3 =
        'data:audio/mpeg;base64,SUQzAwAAAAAAJlRJVDIAAAARAAABMDAtMTAwMHd3LXNpbGVuY2X/+5DEAAAAAAAAAAAAAAAAAAAAAAAA' +
        'SW5mbwAAAA8AAAACAAACgABoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGhoaGj/////////////' +
        '/////////////////////////////////////////////////////////////////////////8AAAAATGF2YzU3LjEwAAAAAAAAAAAAAAAk';
      this.voicePlaybackAudio.src = SILENT_MP3;
      const playResult = this.voicePlaybackAudio.play();
      if (playResult && typeof playResult.then === 'function') {
        playResult.then(() => {
          this.voicePlaybackUnlocked = true;
          console.log('[HUMO Voice] audio playback unlocked');
        }).catch((err) => {
          console.warn('[HUMO Voice] audio unlock failed (will retry on next chunk)', err);
        });
      } else {
        this.voicePlaybackUnlocked = true;
      }
    } catch (err) {
      console.warn('[HUMO Voice] primeVoicePlaybackAudio threw', err);
    }
  }

  private dismissVoiceConsent(): void {
    this.state.showVoiceConsentDialog = false;
    this.render();
  }

  private async startVoiceCall(opts: { resumeSessionId?: string } = {}): Promise<void> {
    if (!this.config.apiUrl || this.state.voiceCallStatus !== 'idle') return;

    this.state.showVoiceConsentDialog = false;
    this.state.voiceCallStatus = 'connecting';
    this.render();

    try {
      let voiceSessionId: string;

      if (opts.resumeSessionId) {
        // Resume path — backend still has this session in grace window.
        // Skip POST /voice/start so we don't create a brand-new session.
        voiceSessionId = opts.resumeSessionId;
        console.log('[HUMO Voice] Attempting resume of voice session', voiceSessionId);
      } else {
        // 1. Create voice session via REST (public endpoint with websiteId auth)
        const res = await fetch(this.buildWidgetApiUrl('/widget/voice/start'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            websiteId: this.config.websiteId,
            sessionId: this.sessionId,
            consentRecording: this.state.voiceCallConsentGiven,
            language: this.config.locale
          })
        });

        if (!res.ok) throw new Error('Failed to start voice session');
        const data = await res.json();
        voiceSessionId = data.sessionId;
      }
      this.state.voiceCallSessionId = voiceSessionId;

      // 2. Connect to /voice socket
      this.voiceSocket = io(`${this.config.apiUrl}/voice`, {
        auth: { type: 'widget', websiteId: this.config.websiteId },
        query: {
          socketContext: 'widget_voice',
          websiteId: this.config.websiteId
        },
        transports: ['polling', 'websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000
      });

      this.voiceSocket.on('connect', () => {
        this.voiceSocket!.emit('voice:session:join', {
          sessionId: voiceSessionId,
          role: 'customer',
          displayName: this.config.visitorName || 'Customer'
        });
      });

      this.voiceSocket.on('voice:session:ready', (d: { participantId: string }) => {
        this.state.voiceCallParticipantId = d.participantId;
        this.state.voiceCallStatus = 'active';
        this.state.voiceCallActive = true;
        this.state.voiceTurnPhase = 'listening';
        this.state.voiceTurnError = null;
        // Persist for resume on route change / refresh. We bump the timestamp
        // on every successful ready so a long call keeps refreshing the TTL.
        this.storeVoiceSession(voiceSessionId);
        this.render();
        this.startVoiceMic(voiceSessionId);
      });

      // Backend fires this the moment smart-endpointing decides the user
      // is done talking. From this point until the AI emits either text
      // (voice:ai:response) or audio (voice:ai:audio_chunk) the UI shows
      // "thinking" — without it the customer stares at "Dinleniyor…"
      // through the 1-3s orchestration window and assumes the call dropped.
      // Watchdog fires 15s later if still no answer, flipping to error.
      this.voiceSocket.on('voice:turn:user_finished', () => {
        this.setVoiceTurnPhase('thinking');
        this.armVoiceTurnTimeout();
      });

      this.voiceSocket.on('voice:ai:response', (d: { text: string }) => {
        this.state.voiceCallAiText = d.text;
        // Text came back — server is alive and replying. Cancel the watchdog
        // but stay in 'thinking' until actual audio arrives (audio is what
        // the user perceives as the AI talking).
        this.clearVoiceTurnTimeout();
        this.render();
      });

      // NEW: Streaming audio chunks for real-time playback
      // NOTE: handoff and the non-streaming socket path emit a single chunk with
      // BOTH audioData AND isFinal:true. Previously we guarded on `!d.isFinal`,
      // which silently dropped that audio. Decouple the two: if the chunk has
      // audio bytes, we queue them regardless of isFinal.
      this.voiceSocket.on('voice:ai:audio_chunk', (d: {
        sessionId: string;
        turnId: string;
        chunkIndex: number;
        audioData?: string;  // base64 encoded audio
        audioFormat?: string;
        isFinal: boolean;
      }) => {
        // First audio chunk = AI started speaking. Cancel watchdog + flip
        // phase so the UI label switches from "Yanıt hazırlanıyor…" to
        // "AI konuşuyor…".
        if (d.audioData) {
          this.clearVoiceTurnTimeout();
          this.setVoiceTurnPhase('speaking');
          try {
            // Decode base64 to Blob
            const binaryString = atob(d.audioData);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const mime = this.getVoiceAudioMimeType(d.audioFormat);
            const audioBlob = new Blob([bytes], { type: mime });
            const audioUrl = URL.createObjectURL(audioBlob);
            this.queueAudio(audioUrl);
            console.log('[HUMO Voice] audio_chunk queued', {
              chunkIndex: d.chunkIndex,
              isFinal: d.isFinal,
              size: bytes.byteLength,
              mime,
              format: d.audioFormat
            });
          } catch (err) {
            console.error('[HUMO Voice] Failed to decode audio chunk:', err);
          }
        }

        if (d.isFinal) {
          console.log('[HUMO Voice] audio_chunk final marker', { chunkIndex: d.chunkIndex });
        }
      });

      // Agent / other peer audio relayed by the backend when they join the
      // call via voice:handoff:accept. Mirrors the voice:ai:audio_chunk
      // pipeline above but for human peers instead of the AI. Without this
      // handler the customer would see "agent joined" but hear nothing when
      // the agent talked.
      this.voiceSocket.on('voice:peer:audio_chunk', (d: {
        sessionId: string;
        fromParticipantId: string;
        fromRole: string;
        chunkIndex: number;
        audioData: string;
        audioFormat?: string;
      }) => {
        if (!d.audioData) return;
        // Don't echo the customer's own audio back to themselves. Backend
        // already excludes the sender via socket.to(), but guard anyway in
        // case the participant id flow ever gets reused.
        if (d.fromParticipantId === this.state.voiceCallParticipantId) return;
        try {
          const binary = atob(d.audioData);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
          // Customer-side capture is Ogg/Opus by default (see startVoiceMic).
          const mime = this.getVoiceAudioMimeType(d.audioFormat ?? 'ogg');
          const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
          this.queueAudio(url);
        } catch (err) {
          console.error('[HUMO Voice] Failed to decode peer audio chunk:', err);
        }
      });

      this.voiceSocket.on('voice:ended', () => {
        this.endVoiceCall();
      });

      this.voiceSocket.on('voice:error', (d: { code?: string; message: string }) => {
        console.error('[HUMO Voice] Server error:', d.code, d.message);
        this.showVoiceError(d.message);
        this.endVoiceCall();
      });

      this.voiceSocket.on('connect_error', (err: Error) => {
        console.error('[HUMO Voice] Socket connection error:', err.message);
        this.showVoiceError(this.config.locale === 'tr' ? 'Sesli arama bağlantısı kurulamadı.' : 'Could not connect to voice call.');
        this.endVoiceCall();
      });

      this.voiceSocket.on('reconnect_failed', () => {
        console.error('[HUMO Voice] Socket reconnect failed');
        this.endVoiceCall();
      });

    } catch (err) {
      console.error('[HUMO Voice] Failed to start:', err);
      this.showVoiceError(this.config.locale === 'tr' ? 'Sesli arama başlatılamadı.' : 'Failed to start voice call.');
      this.state.voiceCallStatus = 'idle';
      this.render();
    }
  }

  private showVoiceError(message: string): void {
    // Show error as a brief system message in the chat
    const errMsg = {
      id: `voice_err_${Date.now()}`,
      text: `⚠️ ${message}`,
      sender: 'bot' as const,
      timestamp: new Date(),
      status: 'delivered' as const,
      type: 'text' as const
    };
    this.state.messages.push(errMsg);
    this.state.isOpen = true;
  }

  /**
   * Lightweight RMS-based VAD. Doesn't try to be Whisper-quality — it's just
   * a "is the user speaking right now?" signal so we can interrupt the AI
   * when they start a new turn. ~150ms reaction time, runs on the main
   * thread because the work is trivial (one analyser node + a single rAF).
   */
  private startBargeInVad(stream: MediaStream): void {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      this.vadContext = ctx;
      this.vadAnalyser = analyser;

      const buf = new Float32Array(analyser.fftSize);
      // Threshold tuned by ear: silence is ~0.005, normal speech ~0.04+.
      // We require sustained speech for ~120ms before triggering barge-in
      // so brief noise (door slam, mic bump) doesn't cut off the AI.
      const TRIGGER_RMS = 0.025;
      const TRIGGER_FRAMES = 6; // ~120ms at 50fps
      let activeFrames = 0;

      const loop = () => {
        if (!this.voiceMediaStream || this.state.voiceCallStatus !== "active") return;
        analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
        const rms = Math.sqrt(sum / buf.length);
        if (rms > TRIGGER_RMS) {
          activeFrames++;
          if (activeFrames >= TRIGGER_FRAMES && this.voiceAudioPlaying) {
            this.interruptAiPlayback();
            activeFrames = 0;
          }
        } else if (activeFrames > 0) {
          activeFrames = Math.max(0, activeFrames - 1);
        }
        this.vadRaf = requestAnimationFrame(loop);
      };
      this.vadRaf = requestAnimationFrame(loop);
    } catch (err) {
      // VAD is a quality-of-life feature, not critical to the call. Log and
      // continue without it.
      console.warn("[HUMO Voice] VAD init failed; barge-in disabled", err);
    }
  }

  private stopBargeInVad(): void {
    if (this.vadRaf !== null) {
      cancelAnimationFrame(this.vadRaf);
      this.vadRaf = null;
    }
    if (this.vadContext) {
      try { void this.vadContext.close(); } catch { /* ignore */ }
      this.vadContext = null;
    }
    this.vadAnalyser = null;
  }

  private startVoiceCallVisualizer(): void {
    try {
      // Reuse VAD analyser if available, otherwise create new one
      if (!this.vadAnalyser) return;

      const analyser = this.vadAnalyser;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const updateVisualizer = () => {
        if (!this.voiceMediaStream || this.state.voiceCallStatus !== 'active') {
          this.state.audioVisualizerData = [];
          this.render();
          return;
        }

        this.visualizerAnimation = requestAnimationFrame(updateVisualizer);
        analyser.getByteFrequencyData(dataArray);

        // Sample 20 bars from frequency data
        const barCount = 20;
        const step = Math.floor(bufferLength / barCount);
        const visualData: number[] = [];

        for (let i = 0; i < barCount; i++) {
          const index = i * step;
          visualData.push(dataArray[index] / 255); // Normalize to 0-1
        }

        this.state.audioVisualizerData = visualData;

        // Update bars directly in DOM
        if (this.container) {
          const bars = this.container.querySelectorAll<HTMLElement>('.humo-visualizer-bar');
          bars.forEach((bar, i) => {
            bar.style.height = `${Math.max(4, (visualData[i] ?? 0) * 100)}%`;
          });
        }
      };

      this.visualizerAnimation = requestAnimationFrame(updateVisualizer);
    } catch (err) {
      console.warn('[HUMO Voice] Visualizer init failed', err);
    }
  }

  private async startVoiceMic(voiceSessionId: string): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      this.voiceMediaStream = stream;
      this.startBargeInVad(stream);
      this.startVoiceCallVisualizer();

      // NO TIMESLICE - manually cycle MediaRecorder to create complete files
      const startRecordingChunk = () => {
        if (!this.voiceMediaStream || !this.voiceSocket?.connected) return;

        // Use Ogg/Opus: better header structure than WebM
        const useOgg = MediaRecorder.isTypeSupported('audio/ogg;codecs=opus');
        const mimeType = useOgg ? 'audio/ogg;codecs=opus' : 'audio/webm;codecs=opus';
        const formatHint = useOgg ? 'ogg' : 'webm';

        const mr = new MediaRecorder(this.voiceMediaStream, { mimeType });
        this.voiceMediaRecorder = mr;

        mr.ondataavailable = (e) => {
          if (!e.data.size || !this.voiceSocket?.connected) return;
          e.data.arrayBuffer().then(ab => {
            this.voiceSocket?.emit('voice:audio:chunk', {
              sessionId: voiceSessionId,
              chunkIndex: this.voiceChunkIndex++,
              audioData: ab,
              audioFormat: formatHint
            });
          });
        };

        mr.onstop = () => {
          // Immediately start next chunk (seamless recording)
          setTimeout(() => startRecordingChunk(), 10);
        };

        // Start WITHOUT timeslice - will create one complete file
        mr.start();

        // Stop after 1 second to get the chunk
        setTimeout(() => {
          if (mr.state === 'recording') mr.stop();
        }, 1000);
      };

      this.voiceChunkIndex = 0;
      startRecordingChunk();
    } catch (err) {
      // Mic unavailable — there is no useful "text-only" voice call. Without a
      // working mic, the server gets no audio, and the UI used to keep showing
      // "Live | Mic On | Listening…" indefinitely (a lie that was confusing
      // testers). We now end the call cleanly and surface the real reason.
      console.warn('[HUMO Voice] Mic unavailable — ending voice call', err);
      const errText = this.config.locale === 'tr'
        ? 'Mikrofona erişilemiyor. Sesli arama başlatılamadı — tarayıcı izinlerinizi kontrol edin.'
        : 'Microphone access denied. Voice call could not start — please check your browser permissions.';
      this.showVoiceError(errText);
      // Tear down the half-open call so the UI doesn't lie about state.
      this.endVoiceCall();
    }
  }

  private endVoiceCall(): void {
    if (this.state.voiceCallSessionId && this.voiceSocket?.connected) {
      this.voiceSocket.emit('voice:end', { sessionId: this.state.voiceCallSessionId });
    }
    // Cancel any pending "AI didn't respond" watchdog so it doesn't fire
    // after the call already ended and flash an error on an empty panel.
    this.clearVoiceTurnTimeout();
    // Explicit end → no resume; otherwise we'd reattach a dead session next
    // time the widget script runs. Transient disconnects (auto-reconnect or
    // widget re-init within the resume TTL) do NOT call endVoiceCall.
    this.clearStoredVoiceSession();
    this.stopBargeInVad();
    if (this.visualizerAnimation) {
      cancelAnimationFrame(this.visualizerAnimation);
      this.visualizerAnimation = null;
    }
    if (this.currentVoiceAudio) {
      try { this.currentVoiceAudio.pause(); } catch { /* ignore */ }
      this.currentVoiceAudio = null;
    }
    // Pause the persistent playback element (don't destroy it — keep the
    // unlock state across calls so a re-start within the same page session
    // doesn't have to re-prime). Setting src="" releases the audio data.
    if (this.voicePlaybackAudio) {
      try { this.voicePlaybackAudio.pause(); } catch { /* ignore */ }
      try { this.voicePlaybackAudio.src = ''; } catch { /* ignore */ }
    }
    this.voiceMediaRecorder?.stop();
    this.voiceMediaStream?.getTracks().forEach(t => t.stop());
    this.voiceSocket?.disconnect();
    this.voiceSocket = null;
    this.voiceMediaRecorder = null;
    this.voiceMediaStream = null;

    this.state.voiceCallActive = false;
    this.state.voiceCallStatus = 'idle';
    this.state.voiceCallSessionId = null;
    this.state.voiceCallParticipantId = null;
    this.state.audioVisualizerData = [];
    this.state.voiceCallMuted = false;
    this.state.voiceCallTranscript = [];
    this.state.voiceCallAiText = null;
    this.state.showVoiceConsentDialog = false;
    this.state.voiceTurnPhase = 'listening';
    this.state.voiceTurnError = null;
    this.voiceAudioQueue = [];
    this.voiceAudioPlaying = false;

    // Add end message and keep chat open
    const endMsg = {
      id: `voice_end_${Date.now()}`,
      text: this.config.locale === 'tr'
        ? 'Sesli arama sona erdi. Yazışmaya devam edebilirsiniz.'
        : 'Voice call ended. You can continue chatting.',
      sender: 'bot' as const,
      timestamp: new Date(),
      status: 'delivered' as const,
      type: 'text' as const
    };
    this.state.messages.push(endMsg);
    this.state.isOpen = true;  // Keep widget open
    this.render();
  }

  private toggleVoiceMute(): void {
    if (!this.voiceMediaStream || !this.state.voiceCallSessionId) return;
    const next = !this.state.voiceCallMuted;
    this.voiceMediaStream.getAudioTracks().forEach(t => { t.enabled = !next; });
    this.state.voiceCallMuted = next;
    if (this.voiceSocket?.connected) {
      this.voiceSocket.emit('voice:mute', { sessionId: this.state.voiceCallSessionId, muted: next });
    }
    this.render();
  }

  // ── Voice turn phase helpers ─────────────────────────────────────────────
  //
  // Single source of truth for the listening/thinking/speaking/error label
  // shown under the call avatar. Avoids scattered render() calls and keeps
  // the phase transitions auditable in one place.

  private setVoiceTurnPhase(phase: 'listening' | 'thinking' | 'speaking' | 'error'): void {
    if (this.state.voiceTurnPhase === phase) return;
    console.log('[HUMO Voice] phase →', phase);
    this.state.voiceTurnPhase = phase;
    if (phase !== 'error') this.state.voiceTurnError = null;
    this.render();
  }

  private armVoiceTurnTimeout(): void {
    this.clearVoiceTurnTimeout();
    this.voiceTurnTimeout = window.setTimeout(() => {
      // 15s elapsed without any AI response. Most likely Bedrock or TTS
      // timed out, or the orchestrator silently skipped. Surface to the
      // user as an actionable error rather than leaving them staring at
      // a "Yanıt hazırlanıyor…" label forever.
      console.warn('[HUMO Voice] turn timeout — no AI response in', this.VOICE_TURN_TIMEOUT_MS, 'ms');
      this.state.voiceTurnError = this.t('voiceCallTimeout');
      this.setVoiceTurnPhase('error');
    }, this.VOICE_TURN_TIMEOUT_MS);
  }

  private clearVoiceTurnTimeout(): void {
    if (this.voiceTurnTimeout) {
      clearTimeout(this.voiceTurnTimeout);
      this.voiceTurnTimeout = null;
    }
  }

  /**
   * Called from playNextAudio when the queue drains (last chunk ended +
   * no more chunks waiting). Returns the UI to "listening" so the customer
   * knows the mic is open again.
   */
  private onVoicePlaybackDrained(): void {
    if (this.state.voiceTurnPhase === 'speaking') {
      this.setVoiceTurnPhase('listening');
    }
  }

  /**
   * Retry handler bound to the error-state CTA. Just resets to listening
   * — the user can speak again and the pipeline kicks off another turn.
   */
  private retryVoiceTurn(): void {
    this.state.voiceTurnError = null;
    this.setVoiceTurnPhase('listening');
  }

  private getVoiceAudioMimeType(format?: string | null): string {
    const f = (format || '').toLowerCase();
    // Opus inside an Ogg container is what browsers actually decode. Bare
    // 'audio/opus' is rejected by Chrome — Ogg is the correct wrapper.
    if (f === 'opus' || f === 'ogg' || f === 'ogg_opus' || f === 'oga') return 'audio/ogg';
    if (f === 'aac' || f === 'm4a' || f === 'mp4') return 'audio/aac';
    if (f === 'wav' || f === 'wave' || f === 'pcm' || f === 'pcm16') return 'audio/wav';
    if (f === 'webm' || f === 'webm_opus') return 'audio/webm';
    // Default to MP3, which is what every TTS provider in this codebase emits.
    return 'audio/mpeg';
  }

  private queueAudio(url: string): void {
    this.voiceAudioQueue.push(url);
    if (!this.voiceAudioPlaying) this.playNextAudio();
  }

  /**
   * Barge-in: stop AI playback the moment the user starts talking again.
   * Without this the conversation feels robotic — user has to wait for the
   * AI to finish even if they started a new turn. With this, talking over
   * the AI cuts it off (just like a real call) and the next mic chunk is
   * what the server transcribes.
   */
  private interruptAiPlayback(): void {
    if (this.currentVoiceAudio) {
      try { this.currentVoiceAudio.pause(); } catch { /* ignore */ }
      try { this.currentVoiceAudio.src = ""; } catch { /* ignore */ }
      this.currentVoiceAudio = null;
    }
    // Drop any queued chunks too — they're stale once the user has spoken.
    for (const u of this.voiceAudioQueue) {
      if (u.startsWith("blob:")) {
        try { URL.revokeObjectURL(u); } catch { /* ignore */ }
      }
    }
    this.voiceAudioQueue = [];
    this.voiceAudioPlaying = false;
    // Tell the server to abandon the current AI turn so it doesn't keep
    // streaming TTS chunks into a queue we just emptied. The handler is
    // already implemented (voice:turn:cancel) — see voiceSocketService.
    this.voiceSocket?.emit("voice:turn:cancel", {
      sessionId: this.state.voiceCallSessionId,
    });
  }

  private playNextAudio(): void {
    if (!this.voiceAudioQueue.length) {
      this.voiceAudioPlaying = false;
      this.currentVoiceAudio = null;
      // Queue drained — AI is done talking, flip the UI label back to
      // "listening" so the customer knows they can speak again.
      this.onVoicePlaybackDrained();
      this.render();
      return;
    }
    this.voiceAudioPlaying = true;
    this.render();
    const url = this.voiceAudioQueue.shift()!;
    const isBlobUrl = url.startsWith('blob:');

    // Reuse the unlock-primed Audio element if it exists. Creating a fresh
    // Audio() per chunk used to break mobile (each new element needs its own
    // user-gesture permission on iOS Safari). The primed element was already
    // unlocked during the call-start tap, so .src reassignment + play() works
    // even though we're now well outside any gesture context.
    let audio = this.voicePlaybackAudio;
    if (!audio) {
      audio = new Audio();
      audio.preload = 'auto';
      audio.setAttribute('playsinline', 'true');
      this.voicePlaybackAudio = audio;
    }
    // Reset state from any previous chunk
    try { audio.pause(); } catch { /* ignore */ }
    audio.currentTime = 0;
    audio.src = url;
    this.currentVoiceAudio = audio;

    // The previous implementation wired both `onerror` and `play().catch()` to
    // the same `cleanup` function. When playback failed (autoplay policy /
    // decode error), cleanup ran twice and recursively called `playNextAudio`
    // — burning through the queue without playing anything. Make cleanup
    // idempotent so a single chunk can only advance the queue once.
    let cleaned = false;
    const cleanup = (cause: string, evt?: any) => {
      if (cleaned) return;
      cleaned = true;
      if (cause !== 'ended') {
        const err = (audio as any).error;
        console.warn('[HUMO Voice] audio cleanup', {
          cause,
          mediaErrorCode: err?.code,
          mediaErrorMessage: err?.message,
          evtName: evt?.name,
          evtMessage: evt?.message
        });
      }
      if (isBlobUrl) {
        try { URL.revokeObjectURL(url); } catch { /* ignore */ }
      }
      // NOTE: don't null out voicePlaybackAudio — we reuse the SAME element
      // across chunks. Only clear currentVoiceAudio for barge-in tracking.
      this.currentVoiceAudio = null;
      // Detach handlers so a delayed event from the now-finished clip doesn't
      // re-enter playNextAudio after we've already moved on.
      audio.onended = null;
      audio.onerror = null;
      this.playNextAudio();
    };
    audio.onended = () => cleanup('ended');
    audio.onerror = () => cleanup('error');
    // load() is a no-op when src is already set, but it makes the resource
    // loading explicit and avoids browsers that defer fetching until first play.
    try { audio.load(); } catch { /* ignore */ }
    audio.play().then(
      () => {
        this.voicePlaybackUnlocked = true;
        console.log('[HUMO Voice] audio playing', { src: url.slice(0, 60) });
      },
      (err) => {
        // If we get NotAllowedError, the unlock didn't take. Surface to user
        // so they can re-tap to grant permission; meanwhile drop this chunk.
        if (err && (err.name === 'NotAllowedError' || /not allowed/i.test(err.message || ''))) {
          this.voicePlaybackUnlocked = false;
          console.warn('[HUMO Voice] play rejected by autoplay policy — needs user gesture');
        }
        cleanup('play_rejected', err);
      }
    );
  }

  private render(): void {
    // Throttle renders to prevent flickering (max 60fps)
    if (this.renderThrottleTimer) {
      this.pendingRender = true;
      return;
    }

    this.renderThrottleTimer = window.setTimeout(() => {
      this.renderThrottleTimer = null;
      if (this.pendingRender) {
        this.pendingRender = false;
        this.render();
      }
    }, 16); // ~60fps

    const wasOpen = this.state.isOpen;
    this._renderDispatch();

    // Auto-scroll if chat is open (after render completes)
    if (this.state.isOpen && wasOpen) {
      this.scrollToBottom();
    }
  }

  // Decides between a full DOM rebuild and the cheap messages-only update.
  // The full rebuild destroys the input textarea (losing focus/caret) and
  // reloads every avatar/attachment image (the visible "jump"). We only pay
  // that cost when something OUTSIDE the messages list actually changed.
  private _renderDispatch(): void {
    if (!this.container) return;

    const signature = this.computeShellSignature();
    const hasShell = !!this.container.querySelector('.humo-chat-window');

    // Fast path: panel already mounted, open, and only messages/typing changed.
    if (this.state.isOpen && hasShell && signature === this.lastShellSignature) {
      this._renderMessages();
      return;
    }

    // Full rebuild.
    this.lastShellSignature = signature;
    this._render();
  }

  // Everything the shell template depends on EXCEPT the messages list and the
  // typing indicator. If any of this changes we must do a full rebuild.
  private computeShellSignature(): string {
    const s = this.state;
    return JSON.stringify([
      s.isOpen,
      s.agentOnline,
      s.unreadCount,
      s.handoffActive,
      s.handoffConversationId,
      s.showVoiceConsentDialog,
      !!s.incomingAgentCall,
      s.incomingAgentCall?.agentName ?? null,
      s.voiceCallActive,
      s.voiceCallStatus,
      s.voiceCallMuted,
      s.voiceTurnPhase,
      s.voiceTurnError,
      // Visualizer bars animate via direct DOM writes (see startRecording /
      // voice handlers), so their length is enough to detect show/hide here.
      s.audioVisualizerData.length > 0,
      s.showContactForm,
      s.contactFormMessage,
      s.contactFormFields?.name,
      s.contactFormFields?.email,
      s.showCsatForm,
      s.csatSubmitted,
      s.csatRating,
      s.showEmojiPicker,
      s.pendingAttachments.map(a => `${a.id}:${a.previewUrl ?? ''}`).join(','),
      s.isRecording,
    ]);
  }

  // ---- Shared message markup (used by full render + fast path) ----

  private renderWelcomeHtml(): string {
    const greeting = this.remoteGreeting || this.t('greeting');
    const title = this.remoteCompanyName || 'Hümo';
    const starters = this.starterQuestions.length > 0 ? `
        <div class="humo-starter-questions">
          ${this.starterQuestions.map(q => `
            <button type="button" class="humo-action-btn humo-starter-btn" data-action="send_message" data-payload="${this.escapeHtml(q)}">${this.escapeHtml(q)}</button>
          `).join('')}
        </div>` : '';
    return `
      <div class="humo-welcome">
        <div class="humo-welcome-icon">${SVG_SPARKLE}</div>
        <h2 class="humo-welcome-title">${this.escapeHtml(title)}</h2>
        <div class="humo-welcome-greeting">${MULTILINGUAL_GREETINGS.join(' · ')}</div>
        <p class="humo-welcome-sub">${this.escapeHtml(greeting)}</p>${starters}
      </div>`;
  }

  private renderMessageHtml(msg: Message): string {
    return `
      <div class="humo-message humo-message-${msg.sender}" data-message-id="${msg.id}">
        ${msg.sender !== 'user' ? `
          <div class="humo-bot-avatar ${msg.sender === 'agent' ? 'humo-agent-avatar-bg' : ''}">
            ${msg.sender === 'bot' ? SVG_SPARKLE : SVG_AGENT_PERSON}
          </div>
          <div class="humo-message-content">
            <div class="humo-message-bubble">
              ${msg.text && msg.text.trim().length > 0 ? `<p>${linkifyEscaped(this.escapeHtml(msg.text))}</p>` : ''}
              ${msg.attachments && msg.attachments.length > 0 ? this.renderMessageAttachments(msg.attachments) : ''}
            </div>
            ${msg.integrationCard ? this.renderIntegrationCard(msg.integrationCard) : ''}
            ${msg.listingCards && msg.listingCards.length > 0 ? renderListingCards(msg.listingCards, this.config.locale, msg.listingMore) : ''}
            <div class="humo-message-attribution">
              ${msg.sender === 'bot' ? (this.config.locale === 'tr' ? 'Hümo · AI Asistan' : 'Hümo · AI Agent') : (this.config.locale === 'tr' ? 'Temsilci' : 'Agent')}
              <span class="humo-attribution-dot"></span>
              ${this.formatRelativeTime(msg.timestamp)}
            </div>
            ${msg.sender === 'bot' && msg.actionButtons && msg.actionButtons.length > 0 ? `
              <div class="humo-action-buttons">
                ${msg.actionButtons.map(btn => `
                  <button type="button" class="humo-action-btn" data-action="${btn.action}" data-payload="${this.escapeHtml(btn.payload || '')}" data-msg-id="${msg.id}">${this.escapeHtml(btn.label)}</button>
                `).join('')}
              </div>
            ` : ''}
            ${msg.sender === 'bot' && msg.id === this.state.helpfulPromptMessageId ? `
              <div class="humo-helpful-prompt">
                <span class="humo-helpful-label">${this.config.locale === 'tr' ? 'Bu yararlı oldu mu?' : 'Was this helpful?'}</span>
                <button type="button" class="humo-helpful-btn humo-helpful-btn-yes" data-msg-id="${msg.id}" data-vote="yes">👍</button>
                <button type="button" class="humo-helpful-btn humo-helpful-btn-no" data-msg-id="${msg.id}" data-vote="no">👎</button>
              </div>
            ` : ''}
          </div>
        ` : `
          <div class="humo-message-bubble">
            ${msg.text && msg.text.trim().length > 0 ? `<p>${this.escapeHtml(msg.text)}</p>` : ''}
            ${msg.attachments && msg.attachments.length > 0 ? this.renderMessageAttachments(msg.attachments) : ''}
            <span class="humo-message-time">
              ${this.formatTime(msg.timestamp)}
              ${msg.status ? this.getStatusIcon(msg.status) : ''}
            </span>
          </div>
        `}
      </div>`;
  }

  private renderTypingRowHtml(): string {
    return `
      <div class="humo-message humo-message-bot humo-typing-row">
        <div class="humo-bot-avatar">${SVG_SPARKLE}</div>
        <div class="humo-message-content">
          <div class="humo-message-bubble humo-typing">
            <div class="humo-typing-dots"><span></span><span></span><span></span></div>
          </div>
        </div>
      </div>`;
  }

  private renderMessagesInnerHtml(): string {
    const welcome = this.state.messages.length === 0 ? this.renderWelcomeHtml() : '';
    const msgs = this.state.messages.map(msg => this.renderMessageHtml(msg)).join('');
    const typing = this.state.isTyping ? this.renderTypingRowHtml() : '';
    return `${welcome}${msgs}${typing}`;
  }

  // Per-message signature: the fields whose change requires re-rendering that
  // one bubble (status tick, text, attachment urls, action/feedback chips).
  // Relative timestamp drift is intentionally ignored — it only refreshes when
  // some other change triggers a render, matching the previous behaviour.
  private messageSignature(msg: Message): string {
    return JSON.stringify([
      msg.text ?? '',
      msg.status ?? '',
      (msg.attachments ?? []).map(a => `${a.id}:${a.url ?? ''}:${a.previewUrl ?? ''}`).join(','),
      !!msg.integrationCard,
      (msg.actionButtons ?? []).map(b => `${b.action}:${b.label}`).join(','),
      msg.id === this.state.helpfulPromptMessageId,
    ]);
  }

  // Fast path: update ONLY the messages list, in place. Existing bubbles (and
  // their already-decoded avatar/attachment images) are left untouched unless
  // their content actually changed, so there's no flicker; the input textarea
  // is never part of this subtree, so focus and caret survive intact.
  private _renderMessages(): void {
    const messagesEl = this.container?.querySelector('.humo-messages');
    if (!messagesEl) {
      // Shell isn't mounted the way we expect — fall back to a full rebuild.
      this._render();
      return;
    }

    const { messages, isTyping } = this.state;

    // Welcome block: present only when there are no messages.
    const welcomeEl = messagesEl.querySelector('.humo-welcome');
    if (messages.length > 0 && welcomeEl) {
      welcomeEl.remove();
    } else if (messages.length === 0 && !welcomeEl) {
      messagesEl.insertAdjacentHTML('afterbegin', this.renderWelcomeHtml());
    }

    // Reconcile message nodes against state, in order.
    const typingEl = messagesEl.querySelector('.humo-typing-row');
    let cursor = messagesEl.querySelector('.humo-message:not(.humo-typing-row)');

    for (const msg of messages) {
      const existing = messagesEl.querySelector(`.humo-message[data-message-id="${this.cssEscape(msg.id)}"]`);
      const sig = this.messageSignature(msg);

      if (!existing) {
        // New message — insert at the current cursor position.
        const node = this.htmlToElement(this.renderMessageHtml(msg));
        if (node) {
          (node as HTMLElement).dataset.sig = sig;
          messagesEl.insertBefore(node, cursor ?? typingEl);
          this.attachMessageActionListeners(node);
        }
      } else {
        if ((existing as HTMLElement).dataset.sig !== sig) {
          // Content changed (e.g. status tick) — replace just this bubble.
          const node = this.htmlToElement(this.renderMessageHtml(msg));
          if (node) {
            (node as HTMLElement).dataset.sig = sig;
            existing.replaceWith(node);
            this.attachMessageActionListeners(node);
          }
        }
        // Advance cursor to the node after this message for correct ordering.
        cursor = existing.nextElementSibling as Element | null;
        if (cursor && cursor.classList.contains('humo-typing-row')) cursor = null;
      }
    }

    // Remove any stale message nodes no longer in state.
    const liveIds = new Set(messages.map(m => m.id));
    messagesEl.querySelectorAll('.humo-message:not(.humo-typing-row)').forEach(node => {
      const id = (node as HTMLElement).dataset.messageId;
      if (id && !liveIds.has(id)) node.remove();
    });

    // Typing indicator: keep exactly one, always last.
    if (isTyping && !typingEl) {
      messagesEl.insertAdjacentHTML('beforeend', this.renderTypingRowHtml());
    } else if (!isTyping && typingEl) {
      typingEl.remove();
    } else if (isTyping && typingEl) {
      messagesEl.appendChild(typingEl); // ensure it stays at the bottom
    }
  }

  private htmlToElement(html: string): Element | null {
    const tpl = document.createElement('template');
    tpl.innerHTML = html.trim();
    return tpl.content.firstElementChild;
  }

  private cssEscape(value: string): string {
    const fn = (window as unknown as { CSS?: { escape?: (v: string) => string } }).CSS?.escape;
    return fn ? fn(value) : value.replace(/["\\\]]/g, '\\$&');
  }

  private _render(): void {
    if (!this.container) return;

    // Safety net: a full rebuild destroys the input textarea. If the user was
    // focused/typing in it when a structural change forced a full render,
    // capture focus + caret + message scroll so we can restore them after.
    const focusState = this.captureInputFocus();

    const { isOpen, unreadCount, agentOnline } = this.state;

    // Launcher icon SVGs
    const icons = {
      chat: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>`,
      message: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>`,
      help: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>`
    };

    const closeIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`;
    const collapseIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"></polyline></svg>`;
    const sendIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>`;
    const sparkleIcon = `<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2L13.8 9.2L21 12L13.8 14.8L12 22L10.2 14.8L3 12L10.2 9.2L12 2Z"/><path d="M20 4L20.6 6.4L23 7L20.6 7.6L20 10L19.4 7.6L17 7L19.4 6.4L20 4Z" opacity="0.55"/><path d="M4 16L4.5 17.5L6 18L4.5 18.5L4 20L3.5 18.5L2 18L3.5 17.5L4 16Z" opacity="0.4"/></svg>`;
    const emojiIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><path d="M8 14s1.5 2 4 2 4-2 4-2"></path><line x1="9" y1="9" x2="9.01" y2="9"></line><line x1="15" y1="9" x2="15.01" y2="9"></line></svg>`;
    const attachIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>`;
    const micIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>`;
    const stopIcon = `<svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"></rect></svg>`;
    const playIcon = `<svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>`;
    const phoneIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .84h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></svg>`;
    const phoneOffIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.68 13.31a16 16 0 003.41 2.6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7 2 2 0 011.72 2v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.42 19.42 0 013.07 9.81M1 1l22 22"/><path d="M5.88 5.88a19.5 19.5 0 005.25 9.11"/></svg>`;
    const micOffIcon = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>`;


    this.container.innerHTML = `
      ${isOpen ? `
        <div class="humo-chat-window">
          <div class="humo-header humo-header-gradient">
            <div class="humo-header-info">
              <div class="humo-header-ai-avatar">
                ${sparkleIcon}
              </div>
              <div class="humo-header-text">
                <span class="humo-header-title">Hümo</span>
                <span class="humo-agent-presence">
                  <span class="humo-presence-dot" style="background: ${agentOnline ? '#4ade80' : '#a78bfa'}"></span>
                  ${agentOnline ? this.t('agentOnline') : this.t('aiAssistant')}
                </span>
              </div>
            </div>
            <div class="humo-header-actions">
              <button class="humo-btn-icon humo-close-btn" aria-label="Collapse">
                ${collapseIcon}
              </button>
            </div>
          </div>

          <div class="humo-messages" role="log" aria-live="polite" aria-label="Chat messages">
            ${this.renderMessagesInnerHtml()}
          </div>

          ${this.state.handoffActive ? `
            <div class="humo-handoff-banner">
              <div class="humo-handoff-banner-header">
                <div class="humo-handoff-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                </div>
                <div>
                  <div class="humo-handoff-title">${this.config.locale === 'tr' ? 'Temsilciye bağlanıyor' : 'Connecting to an agent'}</div>
                  <div class="humo-handoff-subtitle">${this.config.locale === 'tr' ? 'Bir temsilci kısa süre içinde size yardımcı olacak.' : 'An agent will be with you shortly.'}</div>
                </div>
              </div>
              ${this.state.handoffConversationId ? `
                <div class="humo-handoff-conv-id">
                  ${this.config.locale === 'tr' ? 'Konuşma ID:' : 'Conversation ID:'} <strong>${this.escapeHtml(this.state.handoffConversationId.slice(-10))}</strong>
                </div>
              ` : ''}
            </div>
          ` : ''}

          ${this.state.showVoiceConsentDialog ? `
            <div class="humo-voice-consent-backdrop">
              <div class="humo-voice-consent-dialog" role="dialog" aria-modal="true" aria-labelledby="humo-voice-consent-title">
                <div class="humo-voice-consent-icon">${phoneIcon}</div>
                <h3 id="humo-voice-consent-title" class="humo-voice-consent-title">${this.t('voiceConsentTitle')}</h3>
                <p class="humo-voice-consent-body">${this.t('voiceConsentBody')}</p>
                <div class="humo-voice-consent-note">${this.t('voiceConsentPrivacy')}</div>
                <div class="humo-voice-consent-actions">
                  <button type="button" class="humo-voice-consent-cancel">${this.t('voiceConsentCancel')}</button>
                  <button type="button" class="humo-voice-consent-confirm">${this.t('voiceConsentConfirm')}</button>
                </div>
              </div>
            </div>
          ` : ''}

          ${this.state.incomingAgentCall ? `
            <div class="humo-voice-consent-backdrop">
              <div class="humo-voice-consent-dialog" role="dialog" aria-modal="true" aria-labelledby="humo-incoming-call-title" style="text-align:center">
                <div class="humo-voice-consent-icon" style="animation:humoRing 1s ease-in-out infinite">${phoneIcon}</div>
                <h3 id="humo-incoming-call-title" class="humo-voice-consent-title">
                  ${this.config.locale === 'tr' ? '📞 Gelen Sesli Arama' : '📞 Incoming Voice Call'}
                </h3>
                <p class="humo-voice-consent-body">
                  <strong>${this.state.incomingAgentCall.agentName}</strong>
                  ${this.config.locale === 'tr' ? ' sizinle görüşmek istiyor.' : ' wants to talk with you.'}
                </p>
                <div class="humo-voice-consent-actions">
                  <button type="button" class="humo-voice-consent-cancel humo-incoming-call-decline">
                    ${this.config.locale === 'tr' ? 'Reddet' : 'Decline'}
                  </button>
                  <button type="button" class="humo-voice-consent-confirm humo-incoming-call-accept" style="background:#16a34a">
                    ${this.config.locale === 'tr' ? 'Cevapla' : 'Answer'}
                  </button>
                </div>
              </div>
            </div>
          ` : ''}

          ${this.state.voiceCallActive ? `
            <div class="humo-voice-call-panel">
              <div class="humo-voice-call-header">
                <span class="humo-voice-live-dot"></span>
                <div class="humo-voice-call-heading">
                  <span class="humo-voice-call-title">${this.t('voiceCallLabel')}</span>
                  <span class="humo-voice-call-subtitle">${this.state.voiceCallMuted ? this.t('voiceCallMuted') : this.t('voiceCallMicOn')}</span>
                </div>
                <div class="humo-voice-call-actions">
                  <button type="button" class="humo-voice-mute-btn ${this.state.voiceCallMuted ? 'humo-voice-muted' : ''}" aria-label="${this.state.voiceCallMuted ? 'Unmute' : 'Mute'}">
                    ${this.state.voiceCallMuted ? micOffIcon : micIcon}
                  </button>
                  <button type="button" class="humo-voice-end-btn" aria-label="End call">
                    ${phoneOffIcon}
                  </button>
                </div>
              </div>
              <div class="humo-voice-call-meta">
                <span class="humo-voice-pill humo-voice-pill-live">${this.t('voiceCallLive')}</span>
                <span class="humo-voice-pill">${this.state.voiceCallMuted ? this.t('voiceCallMuted') : this.t('voiceCallMicOn')}</span>
              </div>
              <div class="humo-voice-transcript-shell">
                <div class="humo-voice-transcript">
                  ${(() => {
                    // Phase-aware status label. Speaking has its own dedicated
                    // pulsing block below; here we just render the matching
                    // top-line text. Error gets a retry CTA inline.
                    const phase = this.state.voiceTurnPhase;
                    if (phase === 'speaking') {
                      return `<p class="humo-voice-listening humo-voice-state-speaking">${this.t('voiceCallAiSpeaking')}</p>`;
                    }
                    if (phase === 'thinking') {
                      return `
                        <p class="humo-voice-listening humo-voice-state-thinking">
                          <span class="humo-voice-thinking-dot"></span>
                          <span class="humo-voice-thinking-dot"></span>
                          <span class="humo-voice-thinking-dot"></span>
                          ${this.t('voiceCallThinking')}
                        </p>
                      `;
                    }
                    if (phase === 'error') {
                      const msg = this.escapeHtml(this.state.voiceTurnError || this.t('voiceCallError'));
                      return `
                        <p class="humo-voice-listening humo-voice-state-error">${msg}</p>
                        <button type="button" class="humo-voice-retry-btn">${this.t('voiceCallRetry')}</button>
                      `;
                    }
                    // Default: listening
                    return `<p class="humo-voice-listening">${this.t('voiceCallListening')}</p>`;
                  })()}
                </div>
              </div>
              ${this.state.voiceTurnPhase === 'listening' && this.state.audioVisualizerData.length > 0 ? `
                <div class="humo-audio-visualizer">
                  ${this.state.audioVisualizerData.map(val => `
                    <div class="humo-visualizer-bar" style="height: ${Math.max(4, val * 100)}%"></div>
                  `).join('')}
                </div>
              ` : ''}
              ${this.state.voiceTurnPhase === 'speaking' ? `
                <div class="humo-voice-speaking-indicator">
                  <span class="humo-voice-speaking-dot"></span>
                  <span class="humo-voice-speaking-dot"></span>
                  <span class="humo-voice-speaking-dot"></span>
                  <span class="humo-voice-speaking-label">${this.t('voiceCallAiSpeaking')}</span>
                </div>
              ` : ''}
            </div>
          ` : this.state.voiceCallStatus === 'connecting' ? `
            <div class="humo-voice-call-panel humo-voice-connecting">
              <span class="humo-voice-live-dot"></span>
              <div class="humo-voice-connecting-copy">
                <span class="humo-voice-connecting-title">${this.t('voiceCallConnecting')}</span>
                <span class="humo-voice-connecting-subtitle">${this.t('voiceCallConnectingHint')}</span>
              </div>
              <button type="button" class="humo-voice-end-btn humo-voice-end-inline" aria-label="Cancel call">
                ${phoneOffIcon}
              </button>
            </div>
          ` : ''}

          ${this.state.showContactForm ? `
            <div class="humo-contact-form">
              <div class="humo-contact-form-message">${this.escapeHtml(this.state.contactFormMessage)}</div>
              <form class="humo-contact-form-fields">
                ${this.state.contactFormFields.name ? `
                  <input
                    type="text"
                    class="humo-contact-input"
                    name="name"
                    placeholder="${this.t('contactNamePlaceholder')}"
                    autocomplete="name"
                  />
                ` : ''}
                ${this.state.contactFormFields.email ? `
                  <input
                    type="email"
                    class="humo-contact-input"
                    name="email"
                    placeholder="${this.t('contactEmailPlaceholder')}"
                    autocomplete="email"
                  />
                ` : ''}
                <div class="humo-contact-form-actions">
                  <button type="submit" class="humo-contact-submit">${this.t('contactSubmit')} ✓</button>
                  <button type="button" class="humo-contact-skip">${this.t('contactSkip')}</button>
                </div>
              </form>
            </div>
          ` : ''}

          ${this.state.showCsatForm ? `
            <div class="humo-csat-form">
              ${this.state.csatSubmitted ? `
                <div class="humo-csat-thanks">
                  <span class="humo-csat-thanks-icon">✓</span>
                  <p>${this.t('csatThanks')}</p>
                </div>
              ` : `
                <p class="humo-csat-title">${this.t('csatTitle')}</p>
                <div class="humo-csat-stars">
                  ${[1,2,3,4,5].map(n => `
                    <button type="button" class="humo-csat-star ${n <= this.state.csatRating ? 'humo-csat-star-active' : ''}" data-rating="${n}" aria-label="${n} star${n > 1 ? 's' : ''}">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="${n <= this.state.csatRating ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="1.5">
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                    </button>
                  `).join('')}
                </div>
                <textarea class="humo-csat-comment" rows="2" placeholder="${this.t('csatCommentPlaceholder')}">${this.escapeHtml(this.state.csatComment)}</textarea>
                <div class="humo-csat-actions">
                  <button type="button" class="humo-csat-submit" ${this.state.csatRating === 0 ? 'disabled' : ''}>${this.t('csatSubmit')}</button>
                  <button type="button" class="humo-csat-skip">${this.t('csatSkip')}</button>
                </div>
              `}
            </div>
          ` : ''}

          ${this.state.showEmojiPicker ? `
            <div class="humo-emoji-picker" role="dialog" aria-label="Emoji picker">
              <div class="humo-emoji-grid" role="group">
                ${EMOJI_LIST.map(emoji => `
                  <button type="button" class="humo-emoji-btn" data-emoji="${emoji}" aria-label="${emoji}">${emoji}</button>
                `).join('')}
              </div>
            </div>
          ` : ''}

          ${this.state.pendingAttachments.length > 0 ? `
            <div class="humo-attachments-preview">
              ${this.state.pendingAttachments.map(att => `
                <div class="humo-attachment-preview" data-id="${att.id}">
                  ${att.type.startsWith('image/') && att.previewUrl
        ? `<img src="${att.previewUrl}" alt="${this.escapeHtml(att.name)}" />`
        : att.type.startsWith('audio/')
          ? `<div class="humo-audio-preview">
                        <button type="button" class="humo-audio-play" data-url="${att.previewUrl}">
                          ${playIcon}
                        </button>
                        <span class="humo-audio-duration">${this.formatDuration(att.duration || 0)}</span>
                      </div>`
          : `<div class="humo-attachment-icon">📎</div>`
      }
                  ${!att.type.startsWith('audio/') ? `<span class="humo-attachment-name">${this.escapeHtml(att.name)}</span>` : ''}
                  <button type="button" class="humo-attachment-remove" data-id="${att.id}">×</button>
                </div>
              `).join('')}
            </div>
          ` : ''}

          ${this.state.isRecording ? `
            <div class="humo-recording-ui">
              <button type="button" class="humo-recording-cancel">Cancel</button>
              <div class="humo-recording-info">
                <div class="humo-recording-indicator"></div>
                <span class="humo-recording-time">${this.formatDuration(this.state.recordingDuration)}</span>
              </div>
              <div class="humo-audio-visualizer">
                ${this.state.audioVisualizerData.map(val => `
                  <div class="humo-visualizer-bar" style="height: ${val * 100}%"></div>
                `).join('')}
              </div>
              <button type="button" class="humo-recording-stop">
                ${stopIcon}
              </button>
            </div>
          ` : ''}

          ${!this.state.isRecording ? `
            <div class="humo-input-area">
              <form class="humo-input-form">
                ${this.config.enableFileUpload ? `
                  <button type="button" class="humo-btn-attach" aria-label="Attach file">
                    ${attachIcon}
                  </button>
                  <input type="file" class="humo-file-input" hidden multiple accept="${this.config.allowedFileTypes?.join(',') || '*'}" />
                ` : ''}
                ${this.config.enableEmojis ? `
                  <button type="button" class="humo-btn-emoji" aria-label="Add emoji">
                    ${emojiIcon}
                  </button>
                ` : ''}
                <textarea
                  class="humo-input"
                  rows="1"
                  placeholder="${this.config.placeholder || this.t('placeholder')}"
                  autocomplete="off"
                >${this.escapeHtml(this.state.draftText)}</textarea>
                ${this.config.enableVoiceRecording ? `
                  <button type="button" class="humo-btn-mic" aria-label="Record voice message">
                    ${micIcon}
                  </button>
                ` : ''}
                ${this.config.enableVoiceCall && this.config.apiUrl && this.state.voiceCallStatus === 'idle' ? `
                  <button type="button" class="humo-btn-voice-call" aria-label="${this.config.locale === 'tr' ? 'Sesli ara' : 'Start voice call'}" title="${this.t('voiceCallLabel')}">
                    <span class="humo-btn-voice-call-icon">${phoneIcon}</span>
                    <span class="humo-btn-voice-call-label">${this.t('voiceCallTrigger')}</span>
                  </button>
                ` : ''}
                <button type="submit" class="humo-send-btn" aria-label="${this.t('sendButton')}">
                  ${sendIcon}
                </button>
              </form>
            </div>
          ` : ''}

          <div class="humo-footer">
            <a href="${poweredByUrl('chat')}" target="_blank" rel="noopener">${this.t('poweredBy')}</a>
          </div>
        </div>
      ` : ''}

      <button class="humo-launcher ${isOpen ? 'humo-launcher-hidden' : ''}" aria-label="${isOpen ? 'Close chat' : 'Open chat'}" aria-expanded="${isOpen}">
        ${isOpen ? closeIcon : icons[this.config.launcherIcon]}
        ${unreadCount > 0 ? `<span class="humo-badge" aria-label="${unreadCount} unread messages">${unreadCount}</span>` : ''}
      </button>
    `;

    // Attach event listeners
    this.attachEventListeners();

    // Stamp per-message signatures so the next fast-path update can tell which
    // bubbles actually changed (and leave the rest — and their images — alone).
    this.stampMessageSignatures();

    // Restore focus/caret if the user was typing before this full rebuild.
    this.restoreInputFocus(focusState);
  }

  private captureInputFocus(): { selStart: number; selEnd: number } | null {
    if (!this.container) return null;
    const input = this.container.querySelector('.humo-input') as HTMLTextAreaElement | null;
    if (!input || document.activeElement !== input) return null;
    return { selStart: input.selectionStart ?? input.value.length, selEnd: input.selectionEnd ?? input.value.length };
  }

  private restoreInputFocus(focus: { selStart: number; selEnd: number } | null): void {
    if (!focus || !this.container) return;
    const input = this.container.querySelector('.humo-input') as HTMLTextAreaElement | null;
    if (!input) return;
    // preventScroll avoids the viewport jumping to the input on restore.
    input.focus({ preventScroll: true });
    const len = input.value.length;
    try {
      input.setSelectionRange(Math.min(focus.selStart, len), Math.min(focus.selEnd, len));
    } catch { /* setSelectionRange can throw on some input types — ignore */ }
  }

  private stampMessageSignatures(): void {
    const messagesEl = this.container?.querySelector('.humo-messages');
    if (!messagesEl) return;
    for (const msg of this.state.messages) {
      const node = messagesEl.querySelector(`.humo-message[data-message-id="${this.cssEscape(msg.id)}"]`);
      if (node) (node as HTMLElement).dataset.sig = this.messageSignature(msg);
    }
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private escapeAttribute(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private getStatusIcon(status: Message['status']): string {
    const icons: Record<NonNullable<Message['status']>, string> = {
      sending: '○',      // Empty circle (sending)
      sent: '✓',         // Single tick (sent)
      delivered: '✓✓',   // Double tick (delivered)
      read: '✓✓',        // Double tick (read - will be blue via CSS)
      failed: '!'
    };
    const icon = status ? icons[status] : '';
    const className = status === 'read' ? 'humo-status humo-status-read' :
      status === 'delivered' ? 'humo-status humo-status-delivered' :
      status === 'failed' ? 'humo-status humo-status-failed' :
        'humo-status';
    return `<span class="${className}">${this.escapeHtml(icon)}</span>`;
  }

  private syncInputHeight(): void {
    if (!this.container) return;
    const input = this.container.querySelector('.humo-input') as HTMLTextAreaElement | null;
    if (!input) return;

    input.style.height = 'auto';
    const maxHeight = 140;
    const nextHeight = Math.min(input.scrollHeight, maxHeight);
    input.style.height = `${nextHeight}px`;
    input.style.overflowY = input.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }

  private attachEventListeners(): void {
    if (!this.container) return;

    // Launcher click
    const launcher = this.container.querySelector('.humo-launcher');
    launcher?.addEventListener('click', () => this.toggle());

    // Close button
    const closeBtn = this.container.querySelector('.humo-close-btn');
    closeBtn?.addEventListener('click', () => this.close());

    // Emoji button
    const emojiBtn = this.container.querySelector('.humo-btn-emoji');
    emojiBtn?.addEventListener('click', () => {
      this.state.showEmojiPicker = !this.state.showEmojiPicker;
      this.render();
    });

    // Emoji selection
    const emojiButtons = this.container.querySelectorAll('.humo-emoji-btn');
    emojiButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const emoji = btn.getAttribute('data-emoji');
        if (emoji) {
          this.state.draftText = `${this.state.draftText}${emoji}`;
          this.state.showEmojiPicker = false;
          this.render();
          const input = this.container?.querySelector('.humo-input') as HTMLTextAreaElement;
          input?.focus();
        }
      });
    });

    // File upload button
    const attachBtn = this.container.querySelector('.humo-btn-attach');
    const fileInput = this.container.querySelector('.humo-file-input') as HTMLInputElement;
    attachBtn?.addEventListener('click', () => {
      fileInput?.click();
    });

    // File selection
    fileInput?.addEventListener('change', () => {
      if (fileInput.files) {
        Array.from(fileInput.files).forEach(file => {
          this.handleFileSelect(file);
        });
        fileInput.value = '';
      }
    });

    // Remove attachment
    const removeAttachmentBtns = this.container.querySelectorAll('.humo-attachment-remove');
    removeAttachmentBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.getAttribute('data-id');
        if (id) {
          // Revoke object URL before removing
          const attachment = this.state.pendingAttachments.find(a => a.id === id);
          if (attachment?.previewUrl && this.objectUrls.has(attachment.previewUrl)) {
            URL.revokeObjectURL(attachment.previewUrl);
            this.objectUrls.delete(attachment.previewUrl);
          }
          this.state.pendingAttachments = this.state.pendingAttachments.filter(a => a.id !== id);
          this.render();
        }
      });
    });

    // Form submit
    const form = this.container.querySelector('.humo-input-form');
    form?.addEventListener('submit', (e) => {
      e.preventDefault();
      const text = this.state.draftText;
      if (text.trim() || this.state.pendingAttachments.length > 0) {
        this.state.draftText = '';
        this.state.showEmojiPicker = false;
        // The fast render path (messages-only) never touches the textarea, so
        // the typed text would otherwise stay in the box after sending.
        // Clear the live DOM value explicitly and reset its auto-grown height.
        const liveInput = this.container?.querySelector('.humo-input') as HTMLTextAreaElement | null;
        if (liveInput) {
          liveInput.value = '';
          this.syncInputHeight();
          liveInput.focus({ preventScroll: true });
        }
        this.sendMessage(text);
        // Note: pendingAttachments cleared in sendMessage after successful send
      }
    });

    // Typing indicator - send to server
    const input = this.container.querySelector('.humo-input') as HTMLTextAreaElement;
    input?.addEventListener('input', () => {
      if (input) {
        this.state.draftText = input.value;
        this.syncInputHeight();
      }
      this.sendTypingIndicator();
    });
    input?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        const formElement = form as HTMLFormElement | null;
        if (formElement?.requestSubmit) {
          formElement.requestSubmit();
        } else if (formElement) {
          formElement.dispatchEvent(new Event('submit', { cancelable: true }));
        }
      }
    });

    // Microphone button
    const micBtn = this.container.querySelector('.humo-btn-mic');
    micBtn?.addEventListener('click', () => {
      this.startRecording();
    });

    // Recording stop button
    const recordingStopBtn = this.container.querySelector('.humo-recording-stop');
    recordingStopBtn?.addEventListener('click', () => {
      this.stopRecording();
    });

    // Recording cancel button
    const recordingCancelBtn = this.container.querySelector('.humo-recording-cancel');
    recordingCancelBtn?.addEventListener('click', () => {
      this.cancelRecording();
    });

    // Voice Call: start button
    const voiceCallBtn = this.container.querySelector('.humo-btn-voice-call');
    voiceCallBtn?.addEventListener('click', () => {
      this.handleVoiceCallTrigger();
    });

    const voiceConsentConfirm = this.container.querySelector('.humo-voice-consent-confirm');
    voiceConsentConfirm?.addEventListener('click', () => {
      // If an agent-initiated call is ringing, the dedicated accept handler
      // owns this click (it's bound after this one and will fire too, but
      // we don't want to ALSO start a fresh customer-initiated call).
      if (this.state.incomingAgentCall) return;
      this.confirmVoiceConsent();
    });

    // Cancel works for both the consent dialog and the incoming-call decline
    // button (they share the `.humo-voice-consent-cancel` class). When an
    // incoming call dialog is showing, decline; otherwise dismiss consent.
    const voiceConsentCancel = this.container.querySelector('.humo-voice-consent-cancel');
    voiceConsentCancel?.addEventListener('click', () => {
      if (this.state.incomingAgentCall) {
        this.declineIncomingCall();
      } else {
        this.dismissVoiceConsent();
      }
    });

    // Agent-initiated call: Accept button on the incoming-call ring dialog.
    // Has its own dedicated class because clicking it does more than the
    // generic "confirm" button (resumes into the existing voice session).
    const incomingAcceptBtn = this.container.querySelector('.humo-incoming-call-accept');
    incomingAcceptBtn?.addEventListener('click', (ev) => {
      ev.stopPropagation();
      this.acceptIncomingCall();
    });

    // Voice Call: mute toggle
    const voiceMuteBtn = this.container.querySelector('.humo-voice-mute-btn');
    voiceMuteBtn?.addEventListener('click', () => {
      this.toggleVoiceMute();
    });

    // Voice Call: end call
    const voiceEndBtn = this.container.querySelector('.humo-voice-end-btn');
    voiceEndBtn?.addEventListener('click', () => {
      this.endVoiceCall();
    });

    // Voice Call: retry after a turn timeout / error. Just resets the phase
    // to listening so the customer can speak again; the underlying socket
    // stays connected so the next utterance goes through normally.
    const voiceRetryBtn = this.container.querySelector('.humo-voice-retry-btn');
    voiceRetryBtn?.addEventListener('click', () => {
      this.retryVoiceTurn();
    });

    // Message-scoped listeners (audio play, helpful votes, action chips).
    // Bound container-wide here on a full render; the messages-only fast
    // path (_renderMessages) binds them per new/updated message node instead.
    this.attachMessageActionListeners(this.container);

    // Contact form submit
    const contactForm = this.container.querySelector('.humo-contact-form-fields');
    contactForm?.addEventListener('submit', (e) => {
      e.preventDefault();
      const form = e.target as HTMLFormElement;
      const nameInput = form.querySelector('[name="name"]') as HTMLInputElement;
      const emailInput = form.querySelector('[name="email"]') as HTMLInputElement;

      const name = nameInput?.value.trim();
      const email = emailInput?.value.trim();
      void this.submitContact({ name, email });
    });

    // Contact form skip
    const contactSkipBtn = this.container.querySelector('.humo-contact-skip');
    contactSkipBtn?.addEventListener('click', () => {
      this.state.showContactForm = false;
      this.render();
    });

    // CSAT star rating
    const csatStars = this.container.querySelectorAll('.humo-csat-star');
    csatStars.forEach(btn => {
      btn.addEventListener('click', () => {
        const rating = Number(btn.getAttribute('data-rating'));
        if (rating >= 1 && rating <= 5) {
          this.state.csatRating = rating;
          this.render();
        }
      });
    });

    // CSAT comment
    const csatComment = this.container.querySelector('.humo-csat-comment') as HTMLTextAreaElement;
    csatComment?.addEventListener('input', () => {
      this.state.csatComment = csatComment.value;
    });

    // CSAT submit
    const csatSubmitBtn = this.container.querySelector('.humo-csat-submit');
    csatSubmitBtn?.addEventListener('click', () => {
      if (this.state.csatRating > 0 && this.socket?.connected) {
        this.socket.emit('submit_csat', {
          sessionId: this.sessionId,
          conversationId: this.state.csatConversationId,
          rating: this.state.csatRating,
          comment: this.state.csatComment || undefined
        });
        this.state.csatSubmitted = true;
        this.render();
        setTimeout(() => {
          this.state.showCsatForm = false;
          this.render();
        }, 3000);
      }
    });

    // CSAT skip
    const csatSkipBtn = this.container.querySelector('.humo-csat-skip');
    csatSkipBtn?.addEventListener('click', () => {
      this.state.showCsatForm = false;
      this.render();
    });

    this.syncInputHeight();
    this.updateViewportMetrics();
  }

  // Binds the interactive controls that live INSIDE message bubbles. Scoped to
  // `root` so the fast path can bind only the message nodes it just created,
  // and the full render can bind the whole container in one shot.
  private attachMessageActionListeners(root: ParentNode): void {
    // Audio play buttons (voice attachments — also used in attachment preview)
    root.querySelectorAll('.humo-audio-play').forEach(btn => {
      btn.addEventListener('click', () => {
        const url = btn.getAttribute('data-url');
        if (url) {
          const audio = new Audio(url);
          audio.play();
        }
      });
    });

    // "Was this helpful?" thumbs buttons
    root.querySelectorAll('.humo-helpful-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const vote = btn.getAttribute('data-vote');
        const msgId = btn.getAttribute('data-msg-id');
        // Hide the prompt after voting
        this.state.helpfulPromptMessageId = null;
        this.render();
        // Optionally emit vote to server for analytics
        if (this.socket?.connected && msgId) {
          this.socket.emit('message_feedback', {
            sessionId: this.sessionId,
            messageId: msgId,
            vote
          });
        }
      });
    });

    // Action buttons (OFFER_DEMO / SEND_PRICE / HANDOFF chips below bot messages)
    root.querySelectorAll('.humo-action-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.getAttribute('data-action');
        const payload = btn.getAttribute('data-payload') || '';
        if (action === 'send_message' && payload) {
          this.sendMessage(payload);
        } else if (action === 'handoff') {
          // Emit handoff request
          if (this.socket?.connected) {
            this.socket.emit('request_handoff', { sessionId: this.sessionId });
          }
        } else if (action === 'open_url' && payload) {
          window.open(payload, '_blank', 'noopener,noreferrer');
        }
      });
    });
  }

  private sendTypingIndicator(): void {
    if (!this.socket || !this.socket.connected) return;

    // Throttle: only send once per second
    if (this.typingThrottleTimer) return;

    // Send typing=true immediately if not already sent
    if (!this.isTypingSent) {
      this.socket.emit('typing', {
        sessionId: this.sessionId,
        isTyping: true
      });
      this.isTypingSent = true;
    }

    // Set throttle timer
    this.typingThrottleTimer = window.setTimeout(() => {
      this.typingThrottleTimer = null;
    }, 1000);

    // Clear existing debounce timeout
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }

    // Stop typing after 2 seconds of inactivity (debounce)
    this.typingTimeout = window.setTimeout(() => {
      if (this.socket && this.socket.connected && this.isTypingSent) {
        this.socket.emit('typing', {
          sessionId: this.sessionId,
          isTyping: false
        });
        this.isTypingSent = false;
      }
    }, 2000);
  }

  private handleFileSelect(file: File): void {
    // Check file size
    if (file.size > this.config.maxFileSize) {
      const sizeMB = (this.config.maxFileSize / (1024 * 1024)).toFixed(1);
      this.showError(`File too large. Maximum size is ${sizeMB}MB.`);
      return;
    }

    // Check file type
    if (this.config.allowedFileTypes.length > 0) {
      const isAllowed = this.config.allowedFileTypes.some(pattern => {
        if (pattern.startsWith('.')) {
          // Extension match
          return file.name.toLowerCase().endsWith(pattern.toLowerCase());
        } else if (pattern.includes('*')) {
          // MIME type pattern (e.g., "image/*")
          const regex = new RegExp('^' + pattern.replace('*', '.*') + '$');
          return regex.test(file.type);
        } else {
          // Exact MIME type
          return file.type === pattern;
        }
      });

      if (!isAllowed) {
        this.showError(`File type not allowed. Please select a valid file.`);
        return;
      }
    }

    const attachment: Attachment = {
      id: `att_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      name: file.name,
      type: file.type,
      size: file.size,
      blob: file
    };

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const previewUrl = e.target?.result as string;
        this.objectUrls.add(previewUrl);
        attachment.previewUrl = previewUrl;
        this.state.pendingAttachments.push(attachment);
        this.render();
      };
      reader.readAsDataURL(file);
    } else {
      this.state.pendingAttachments.push(attachment);
      this.render();
    }
  }

  private showError(message: string): void {
    // Create temporary error notification
    if (!this.container) return;

    const errorEl = document.createElement('div');
    errorEl.className = 'humo-error-notification';
    errorEl.textContent = message;
    errorEl.setAttribute('role', 'alert');
    errorEl.setAttribute('aria-live', 'assertive');

    this.container.appendChild(errorEl);

    setTimeout(() => {
      errorEl.style.opacity = '0';
      setTimeout(() => errorEl.remove(), 300);
    }, 5000);
  }

  // Public API
  public open(): void {
    this.state.isOpen = true;
    this.state.unreadCount = 0;
    this.render();
    this.updateViewportMetrics();
    this.scrollToBottom();
  }

  public close(): void {
    this.state.isOpen = false;
    this.state.showVoiceConsentDialog = false;
    this.render();
    this.updateViewportMetrics();
  }

  public toggle(): void {
    if (this.state.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  public setVisitor(name: string, email?: string): void {
    this.config.visitorName = name;
    if (email) this.config.visitorEmail = email;
  }

  /** The member signed in and the place chosen on the site; null clears them. Goes with the next message. */
  public setContext(context: SiteContext | null): void {
    this.config.context = cleanContext(context);
  }

  public sendUserMessage(text: string): void {
    this.sendMessage(text);
  }

  public destroy(): void {
    this.unbindViewportEvents();

    // Close Socket.IO connection
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    // Clear timers
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }
    if (this.typingThrottleTimer) {
      clearTimeout(this.typingThrottleTimer);
      this.typingThrottleTimer = null;
    }
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
    if (this.visualizerAnimation) {
      cancelAnimationFrame(this.visualizerAnimation);
      this.visualizerAnimation = null;
    }

    // Stop recording if active
    if (this.state.isRecording) {
      this.cancelRecording();
    }

    // Revoke all object URLs
    this.objectUrls.forEach(url => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        // Ignore errors
      }
    });
    this.objectUrls.clear();

    // Abort any pending requests
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    // Remove container
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }
}

// Capture script reference immediately (before async/DOMContentLoaded loses it)
const _currentScript = document.currentScript as HTMLScriptElement | null;

function isLikelyWidgetScript(script: HTMLScriptElement): boolean {
  if (script.getAttribute('data-humo-widget') === 'true' || script.id === 'humo-widget-script') {
    return true;
  }

  const src = script.getAttribute('src') || '';
  return src.includes('humo-widget.js') || src.includes('/widget/humo-widget');
}

function findWidgetScript(): HTMLScriptElement | null {
  if (_currentScript?.getAttribute('data-website-id')) {
    return _currentScript;
  }

  const markedScript = document.querySelector('script[data-humo-widget="true"][data-website-id]') as HTMLScriptElement | null;
  if (markedScript) {
    return markedScript;
  }

  const scriptCandidates = Array.from(
    document.querySelectorAll('script[data-website-id][src]')
  ) as HTMLScriptElement[];

  for (let i = scriptCandidates.length - 1; i >= 0; i--) {
    if (isLikelyWidgetScript(scriptCandidates[i])) {
      return scriptCandidates[i];
    }
  }

  const fallbackScripts = document.querySelectorAll('script[data-website-id]');
  if (fallbackScripts.length > 0) {
    return fallbackScripts[fallbackScripts.length - 1] as HTMLScriptElement;
  }

  return null;
}

function deriveApiUrlFromScript(script: HTMLScriptElement | null): string | undefined {
  if (!script) {
    return undefined;
  }

  const explicitApiUrl = script.getAttribute("data-api-url");
  if (explicitApiUrl) {
    return explicitApiUrl;
  }

  const src = script.getAttribute("src");
  if (!src) {
    return undefined;
  }

  try {
    return new URL(src, window.location.href).origin;
  } catch {
    return undefined;
  }
}

// Auto-initialize from script tag
function autoInit(): void {
  const script = findWidgetScript();

  if (!script) {
    console.warn('[HUMO Widget] No script tag found with data-website-id. Widget not initialized.');
    return;
  }

  const websiteId = script.getAttribute('data-website-id');
  const apiUrl = deriveApiUrlFromScript(script);

  if (websiteId) {
    try {
      console.log('[HUMO Widget] Initializing with websiteId:', websiteId.substring(0, 12) + '...');
      (window as any).Humo = new HumoWidget({
        websiteId,
        apiUrl,
        position: (script.getAttribute('data-position') as any) || 'bottom-right',
        primaryColor: script.getAttribute('data-color') || '#6366f1',
        locale: (script.getAttribute('data-locale') as any) || 'en'
      });
    } catch (error) {
      console.error('[HUMO Widget] Initialization failed.', error);
    }
  } else {
    console.warn('[HUMO Widget] Script tag found but data-website-id is empty.');
  }
}

// Run auto-init when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInit);
} else {
  autoInit();
}

// Export for manual initialization
export { HumoWidget };
export default HumoWidget;
